import type { en } from './en.ts';

/**
 * Simplified Chinese (zh-CN) dictionary for the Nxtpath console.
 *
 * Key space and key ORDER mirror `en.ts` exactly — `test/i18n.test.tsx`
 * fails the build on any divergence (missing key, extra key, reordering,
 * empty value, or mismatched `{placeholder}` set). Keep new keys in the
 * same section and the same position in both files.
 *
 * Copy style (DESIGN.md §7, Chinese half)
 * - Field-ledger tone applies in Chinese too: short declarative
 *   sentences, no exclamation marks, no 「请」 anywhere — the English
 *   never says "please" either, so validation hints instruct plainly
 *   (「输入别名」) or state the requirement (「邮箱为必填项」).
 * - Sentence case is an English-only rule; Chinese has no letter case.
 * - The wordmark stays Latin: 「Nxtpath 控制台」, never transliterated.
 * - Full-width punctuation for Chinese prose; keep ASCII around code,
 *   identifiers, and numbers. `test/i18n.test.tsx` enforces the narrow,
 *   unambiguous half of this: ASCII `, ? : ;` may never sit directly
 *   beside a Han glyph. Full-width parentheses in prose (「（可选）」)
 *   are NOT machine-checked — an ASCII `(` can legitimately belong to a
 *   code or number span — so they stay a review-time convention.
 * - Terminology, kept uniform by hand: 「API 密钥」 (never 「API key」 /
 *   「API Key」), 「Token」 with no plural 「Tokens」, 「返回账单」 for
 *   every "Back to billing" CTA.
 */
export const zhCN: Readonly<Record<keyof typeof en, string>> = {
  // ── <head> metadata ─────────────────────────────────────────────
  'meta.title': 'Nxtpath 控制台',
  'meta.description': 'Nxtpath 开发者控制台：管理 API 密钥、查看使用情况、充值余额并邀请团队成员。',

  // ── Common / shared ──────────────────────────────────────────────
  'common.brand.title': 'Nxtpath 控制台',
  'common.session.checking': '正在检查会话…',
  'common.back_to_login': '返回登录',
  'common.technical_details.title': '技术信息',
  'common.technical_details.show': '展开',
  'common.technical_details.hide': '收起',
  'api_key.unnamed': '未命名密钥',
  'export.idle': '导出 Excel',
  'export.working': '正在导出…',
  'export.done': '已导出 Excel',
  'export.error': '导出失败。',
  'export.cap': '范围太大，收窄时间再导',
  'export.working_rows': '正在导出… 已获取 {rows} 行',
  'export.rate_unavailable': '{months} 的结算汇率尚未发布，暂不能导出',
  // 「请」 is banned in this catalog (DESIGN.md §7). The instruction is the
  // same one the English states with "Please contact us."
  'export.unknown_models': '以下模型尚未配置结算币种，暂不能导出：{models}。联系我们。',
  'export.aggregate_cross_month_rate':
    '{months} 汇率不同，汇总金额无法用同一汇率折算，按单月分别导出',

  // ── Login ───────────────────────────────────────────────────────
  'login.eyebrow': '登录',
  'login.subtitle': '登录你的 Nxtpath 账号',
  'login.description': '登录后即可管理 API 密钥、余额、使用情况和团队成员。',
  'login.email': '邮箱',
  'login.password': '密码',
  'login.submit': '登录',
  'login.submitting': '登录中…',
  'login.need_account': '还没有账号？立即注册',
  'login.have_invite_code': '有邀请码？直接激活',
  'login.forgot': '忘记密码？',
  'login.error.empty_fields': '邮箱和密码为必填项',

  // ── Login · email sign-in code (epic #1475) ─────────────────────
  // The conditional "If … has an account" phrasing is deliberate: the
  // request endpoint answers identically for registered and unknown
  // addresses, and the copy must not give away what the API withholds.
  'login.mode.label': '登录方式',
  'login.mode.password': '密码登录',
  'login.mode.code': '邮箱验证码',
  'login.code.send': '发送验证码',
  'login.code.sending': '发送中…',
  'login.code.label': '6 位验证码',
  'login.code.ack': '若 {email} 已注册，验证码已发出，{minutes} 分钟内有效。',
  'login.code.ack_resent': '已重新发送。若 {email} 已注册，新验证码 {minutes} 分钟内有效。',
  'login.code.resend': '重新发送',
  'login.code.change_email': '换个邮箱',
  'login.code.error.empty_email': '邮箱为必填项',
  'login.code.error.format': '验证码是 6 位数字',
  'login.code.error.invalid': '验证码不正确，检查后重试',
  'login.code.error.expired': '验证码已过期，重新获取一个',
  'login.code.error.attempts_exhausted': '尝试次数过多，重新获取验证码',
  'login.code.error.send_rate_limited': '发送太频繁了，稍后再试',
  'login.code.error.verify_rate_limited': '尝试太频繁了，稍后再试',
  'login.code.error.account_suspended': '该账号已被停用，联系你的客户经理',
  'login.code.error.account_deleted': '该账号已注销',

  // ── Activate (invite-code activation, epic #1475) ───────────────
  'activate.subtitle': '用邀请码开通账号',
  'activate.description': '填写邮箱和销售给你的邀请码，即可开通账号并领取体验金。',
  'activate.email': '邮箱',
  'activate.invite_code': '邀请码',
  'activate.submit': '激活账号',
  'activate.submitting': '激活中…',
  'activate.have_account': '已有账号？去登录',
  'activate.no_code': '没有邀请码？直接注册',
  'activate.error.empty_fields': '邮箱和邀请码为必填项',
  'activate.error.code_invalid': '邀请码无效或已停用，向你的销售确认',
  'activate.error.quota_exhausted': '该邀请码今天的名额已用完，明天再试或联系你的销售',
  'activate.error.email_taken': '该邮箱已注册',
  'activate.error.email_taken_cta': '直接登录',
  'activate.error.rate_limited': '操作太频繁了，稍后再试',
  'activate.success.title': '账号已开通',
  'activate.success.description': '体验金已到账，可以直接开始调用。',
  'activate.success.grant_title': '激活成功',
  'activate.success.grant_body': '体验金 {amount} 已到账，有效期至 {expiresAt}。',
  'activate.success.passwordless_hint': '该账号无需密码，下次用「邮箱验证码」登录即可。',
  'activate.success.cta': '进入控制台',

  // ── Register ────────────────────────────────────────────────────
  'register.eyebrow': '创建账号',
  'register.subtitle': '创建 Nxtpath 账号',
  'register.description': '先创建你的账号，后续可以邀请团队成员一起使用。',
  'register.email': '邮箱',
  'register.password_with_hint': '密码（10–128 位）',
  'register.workspace_name': '团队名称（可选）',
  'register.workspace_placeholder': '例如：支付服务 / 数据平台 / 我的团队',
  'register.submit': '创建账号',
  'register.submitting': '创建中…',
  'register.error.empty_fields': '邮箱和密码为必填项',
  'register.error.password_too_short': '密码长度至少 10 位',
  'register.error.email_taken':
    '该邮箱已注册。尚未完成邮箱验证的话，查收验证邮件，或在登录页重新发送验证链接。',
  'register.error.email_taken_cta': '去登录',
  'register.error.invalid_email': '邮箱格式不正确',
  'register.error.password_length': '密码长度需为 10–128 位',
  'register.success.title': '查收邮件',
  'register.success.body':
    '我们已将验证链接发送至 {email}。打开邮件里的链接后，就可以继续进入控制台。',
  'register.success.resend_ack':
    '新的验证链接已发送。若仍未收到，检查垃圾邮件，或稍等一分钟后再试。',
  'register.success.resend_hint': '如果暂时没收到，可以重新发送一次。',
  'register.resend.submit': '重发链接',
  'register.resend.submitting': '发送中…',
  'register.already_have_account': '已有账号？立即登录',

  // ── Forgot password ─────────────────────────────────────────────
  'forgot.eyebrow': '找回访问权限',
  'forgot.subtitle': '找回登录密码',
  'forgot.description': '输入注册邮箱，我们会把重置链接发送给你。',
  'forgot.email': '邮箱',
  'forgot.submit': '发送重置链接',
  'forgot.submitting': '发送中…',
  'forgot.error.empty_email': '邮箱为必填项',
  'forgot.success.title': '查收邮件',
  'forgot.success.body': '若 {email} 存在对应账号，我们已发送重置链接。链接 24 小时内有效。',

  // ── Reset password ──────────────────────────────────────────────
  'reset.eyebrow': '新密码',
  'reset.subtitle': '重新设置密码',
  'reset.description': '保存后就可以用新密码重新登录。',
  'reset.new_password': '新密码（10–128 位）',
  'reset.confirm_password': '确认新密码',
  'reset.submit': '保存新密码',
  'reset.submitting': '保存中…',
  'reset.error.missing_token': '重置链接中缺少令牌',
  'reset.error.password_too_short': '密码长度至少 10 位',
  'reset.error.password_mismatch': '两次输入的密码不一致',
  'reset.error.token_banner': '缺少重置令牌，直接点击邮件中的链接，或重新申请一个。',
  'reset.success.title': '密码已更新',
  'reset.success.body': '密码已更新，用新密码登录。',
  'reset.success.sign_in': '去登录',

  // ── Verify email ────────────────────────────────────────────────
  'verify.subtitle': '完成邮箱验证',
  'verify.description': '验证完成后，点击按钮进入控制台。',
  'verify.success.title': '邮箱验证成功',
  'verify.success.body': '你的邮箱已完成验证，账号可以正常使用了。',
  'verify.success.cta': '进入控制台',
  'verify.missing_token': '此链接缺少验证令牌，直接打开邮件中的链接，或回到登录页重新申请。',
  'verify.pending': '正在验证邮箱…',
  'verify.error.title': '验证失败',
  'verify.resend.label': '申请新的验证链接',
  'verify.resend.submit': '发送新链接',
  'verify.resend.submitting': '发送中…',
  'verify.resend.ack.title': '查收邮件',
  'verify.resend.ack.body': '若该邮箱账号尚未验证，新的验证链接已在发送途中。',

  // ── Invitation ──────────────────────────────────────────────────
  'invite.eyebrow': '团队邀请',
  'invite.subtitle': '加入团队',
  'invite.description': '先确认你要加入的团队，再继续完成加入流程。',
  'invite.loading': '正在加载邀请…',
  'invite.error.missing_token': '链接中缺少邀请令牌',
  'invite.error.title': '邀请不可用',
  'invite.invite_to': '邀请你加入',
  'invite.role': '角色',
  'invite.invitee_email': '受邀邮箱',
  'invite.invited_by': '邀请者',
  'invite.expires': '过期时间',
  'invite.password_with_hint': '密码（10–128 位）',
  'invite.display_name': '显示名称（可选）',
  'invite.display_name_placeholder': '默认使用邮箱前缀',
  'invite.session_match': '你正以 {email} 登录。点击接受即可加入 {org}。',
  'invite.session_mismatch.body':
    '当前以 {current} 登录，但本邀请指向 {target}。输入该账号密码以接受邀请，或',
  'invite.session_mismatch.switch_link': '切换账号',
  'invite.session_mismatch.suffix': '。',
  'invite.accept': '接受邀请',
  'invite.join': '加入团队',
  'invite.accepting': '接受中…',
  'invite.cancel': '取消',
  'invite.error.password_too_short': '密码长度至少 {min} 位',
  'invite.error.password_too_long': '密码长度最多 {max} 位',
  'invite.toast.accepted': '邀请已接受',

  // ── Not found ──────────────────────────────────────────────────
  'notfound.title': '页面未找到',
  'notfound.body': '此控制台中不存在你访问的页面。',
  'notfound.cta': '返回仪表盘',

  // ── Shell chrome ───────────────────────────────────────────────
  'shell.brand.subtitle': 'API 控制台',
  'shell.brand.supporting': '在一个地方管理你的密钥、余额、使用情况和团队成员。',
  'shell.nav.aria_label': '主导航',
  'shell.nav.dashboard': '总览',
  'shell.nav.keys': '密钥',
  'shell.nav.usage': '使用情况',
  'shell.nav.organization': '组织',
  'shell.nav.requests': '请求日志',
  'shell.nav.optimization': '优化',
  'shell.nav.pricing': '模型定价',
  'shell.nav.billing': '余额与账单',
  'shell.nav.settings': '账号与团队',
  'shell.nav.docs': '接入文档',
  'shell.nav.adminApi': '组织管理 API',
  'shell.nav.status': '服务状态',
  'shell.nav.tickets': '工单',
  'shell.nav.open': '打开导航',
  'shell.nav.close': '关闭导航',
  'shell.sidebar.collapse': '收起侧栏',
  'shell.sidebar.expand': '展开侧栏',
  'shell.cmdk.trigger': '搜索或跳转…',
  'shell.cmdk.placeholder': '搜索页面，或粘贴请求 ID',
  'shell.cmdk.empty': '无匹配结果',
  'shell.cmdk.aria_label': '命令面板',
  'shell.cmdk.footer_hint': '↑↓ 移动 · ↵ 打开 · esc 关闭',
  'shell.cmdk.group.recent': '最近访问',
  'shell.cmdk.group.navigation': '导航',
  'shell.cmdk.group.requests': '请求日志',
  'shell.cmdk.open_request': '打开请求 {id}',
  'shell.cmdk.hint.req': '请求',
  'shell.bell.aria': '通知',
  'shell.bell.aria_unread': '通知（{count} 条未读）',
  'shell.header.tenant_chip': 'Nxtpath 租户',
  'shell.org_chip.title': '当前团队',
  'shell.page.console_fallback': '控制台',
  'shell.session.active': '当前会话',
  'shell.session.verified': '邮箱已验证',
  'shell.session.unverified': '邮箱待验证',
  'shell.session.signed_in': '已登录',
  'shell.logout': '退出登录',

  // ── Dashboard ──────────────────────────────────────────────────
  'dashboard.title': '总览',
  'dashboard.subtitle': '快速查看余额、用量和最近的变化。',
  'dashboard.subtitle_no_billing': '快速查看用量和最近的变化。',
  'dashboard.stat.balance.label': '可用余额',
  'dashboard.stat.balance.updated': '更新于 {timestamp}',
  'dashboard.stat.balance.unavailable': '余额不可用',
  'dashboard.stat.balance.loading': '加载中…',
  'dashboard.stat.balance.zero_cta': '余额已用尽——立即充值',
  'dashboard.stat.balance.overdrawn': '余额已透支',
  // 只说这个值看不到，绝不提金额或方向：看到这句话的人，
  // 这张卡片背后的请求根本没有发出去。
  'dashboard.stat.balance.no_access': '余额仅所有者和管理员可见。',
  'dashboard.stat.cost.label': '本月',
  'dashboard.stat.cost.tokens': '本月你已使用 {tokens} Token',
  'dashboard.stat.cost.tokens_org': '本月组织内共使用 {tokens} Token',
  'dashboard.stat.cost.unavailable': '用量不可用',
  'dashboard.stat.cost.loading': '加载中…',
  'dashboard.stat.keys.label': '可用密钥',
  'dashboard.stat.keys.description': '当前仍可使用的密钥',
  'dashboard.stat.keys.description_org_cta': '组织内的全部密钥——查看成员目录',
  'dashboard.stat.keys.unavailable': '密钥不可用',
  'dashboard.stat.keys.empty_cta': '还没有可用密钥——创建第一把',
  // 只说这个值看不到，绝不提数量或去 /keys：看到这句话的人，
  // 这张卡片背后的请求根本没有发出去。
  'dashboard.stat.keys.no_access': '密钥仅所有者、管理员和成员可见。',
  'dashboard.stat.last_request.label': '最近请求',
  'dashboard.stat.last_request.empty': '还没有请求',
  'dashboard.stat.last_request.hint': '有新请求到达时会自动更新',
  'dashboard.stat.last_request.empty_cta': '创建密钥后即可开始发送请求',
  'dashboard.status.caption': '服务状态',
  'dashboard.status.overall.healthy': '全部服务正常',
  'dashboard.status.overall.busy': '部分服务繁忙',
  'dashboard.status.overall.unavailable': '部分服务不可用',
  'dashboard.status.updated': '更新于 {timestamp}',
  'dashboard.status.view': '查看服务状态',
  'dashboard.status.error': '无法加载服务状态。',
  'dashboard.status.empty': '暂无服务数据',
  'dashboard.quick.caption': '快捷操作',
  'dashboard.quick.create_key': '创建密钥',
  'dashboard.quick.topup': '充值',
  'dashboard.quick.requests': '请求诊断',
  'dashboard.quick.pricing': '模型价格',
  'dashboard.trend.requests.caption': '近 7 日请求',
  'dashboard.trend.spend.caption': '近 7 日支出',
  'dashboard.trend.view_usage': '查看用量分析',
  'dashboard.trend.error': '无法加载趋势数据。',
  'dashboard.trend.empty': '暂无数据',
  'dashboard.trend.incomplete': '今日未完整',
  'dashboard.trend.incomplete_note': '今日仍在记录中，最后一个点尚未完整。',
  'dashboard.activity.title': '最近请求',
  'dashboard.activity.view_all': '查看全部用量',
  'dashboard.activity.loading': '正在加载最近活动…',
  'dashboard.activity.error': '无法加载最近活动。',
  'dashboard.retry': '重试',
  'dashboard.activity.empty': '最近 7 天还没有请求记录。',
  'dashboard.activity.empty_cta': '先创建密钥，再发送第一个请求',
  'dashboard.activity.column.hour': '小时',
  'dashboard.activity.column.requests': '请求',
  'dashboard.activity.column.usage': '用量',
  'dashboard.activity.column.cost': '费用',
  'dashboard.activity.unit.tokens': 'Token',
  'dashboard.top_keys.title': '本月使用较多的密钥',
  'dashboard.top_keys.manage': '管理密钥',
  'dashboard.top_keys.loading': '正在加载密钥用量…',
  'dashboard.top_keys.error': '无法加载密钥用量。',
  'dashboard.top_keys.empty.chip': '暂无用量',
  'dashboard.top_keys.empty.hint': '密钥开始承接请求后会出现在这里。',
  'dashboard.top_keys.empty_cta': '创建密钥',
  'dashboard.top_keys.column.key': '密钥',
  'dashboard.top_keys.column.requests': '请求',
  'dashboard.top_keys.column.cost': '费用',

  // ── API keys page ──────────────────────────────────────────────
  'keys.title': 'API 密钥',
  'keys.subtitle': '创建并管理你的应用调用 Nxtpath 所使用的密钥。',
  'keys.create': '创建密钥',
  'keys.base_url.title': '接入地址',
  'keys.base_url.description': '在 SDK、客户端或服务端配置里使用这个 base URL。',
  'keys.loading': '正在加载密钥…',
  'keys.error': '无法加载密钥',
  'keys.empty': '还没有密钥——创建一把即可开始调用。',
  'keys.copied': '已复制',
  'keys.click_to_copy': '点击复制',
  'keys.show_revoked': '显示已停用（{count}）',
  'keys.column.prefix': '前缀',
  'keys.column.name': '名称',
  'keys.column.spend_limits': '消费限额',
  'keys.column.ip_allowlist': 'IP 白名单',
  'keys.column.created': '创建时间',
  'keys.column.last_used': '最近使用',
  'keys.column.actions': '操作',
  'keys.status.revoked': '已停用',
  'keys.status.active': '可用',
  'keys.never_used': '从未',
  'keys.save': '保存',
  'keys.cancel': '取消',
  'keys.rename': '编辑',
  'keys.revoke': '删除',
  'keys.row_error.name_required': '名称不能为空',
  'keys.row_error.network': '网络错误',
  'keys.limit.none': '不限',
  'keys.limit.daily.label': '日',
  'keys.limit.weekly.label': '周',
  'keys.limit.monthly.label': '月',
  'keys.limit.daily.placeholder': '¥ / 日',
  'keys.limit.weekly.placeholder': '¥ / 周',
  'keys.limit.monthly.placeholder': '¥ / 月',
  'keys.limit_loading': '正在读取汇率…',
  'keys.limit_error.invalid': '消费限额必须是非负的人民币金额。',
  'keys.limit_error.zero': '限额不能为 0。留空表示不限制；要停止这把密钥的消费，吊销它即可。',
  'keys.limit_error.rate_unavailable': '暂时无法获取展示汇率，稍后重试。',
  'keys.allowlist.none': '不限',
  'keys.allowlist.count': '{count} 条',
  'keys.allowlist.placeholder': '203.0.113.7\n2001:db8::/32',
  'keys.allowlist.hint':
    '每行一条 IPv4/IPv6 地址或 CIDR 网段。设置后，只有来源 IP 命中名单的请求才能使用这把密钥；留空则不限制。',
  'keys.allowlist_error.invalid_entry': '第 {line} 行：“{entry}”不是合法的 IP 地址或 CIDR。',
  'keys.allowlist_error.too_many': 'IP 白名单最多 {max} 条。',
  'keys.create_modal.title': '创建 API 密钥',
  'keys.create_modal.description':
    '给这把密钥起一个以后能认出来的名称。完整密钥只在创建后显示一次，出现时立即保存。模型映射之后随时可以调整。',
  'keys.create_modal.name_label': '密钥名称（可选）',
  'keys.create_modal.name_placeholder': 'production-backend',
  'keys.create_modal.name_default': '密钥 {date}',
  'keys.create_modal.limits_label': '消费限额（人民币，可选）',
  'keys.create_modal.limits_hint':
    '留空表示该档不限。每档上限都按计量后的用量判定，触碰上限的那次请求仍会正常完成，密钥在该用量计量完成后随即暂停，待周期滚动或调高上限后自动恢复。',
  'keys.create_modal.allowlist_label': 'IP 白名单（可选）',
  'keys.create_modal.template_label': '从命名空间开始（可选）',
  'keys.create_modal.template_none': '不预设映射',
  'keys.create_modal.template_hint':
    '创建后，服务端会原子地映射该命名空间当前的 {count} 个模型（别名 = 模型名）。',
  'keys.create_modal.catalog_error': '模型目录加载失败。你仍然可以创建不带预设映射的密钥。',
  'keys.create_modal.submit': '创建密钥',
  'keys.create_modal.submitting': '创建中…',
  'keys.create_modal.cancel': '取消',
  'keys.template.failure': '密钥已创建，但命名空间映射未生效：{error}',
  'keys.mappings.button': '模型映射',
  'keys.mappings.eyebrow': '密钥 · 模型映射',
  'keys.mappings.title': '模型映射',
  'keys.mappings.subtitle': '把客户端发送的模型名映射到目录中的模型。密钥：{key}',
  'keys.mappings.close': '关闭模型映射',
  'keys.mappings.loading': '正在加载映射…',
  'keys.mappings.error': '无法加载模型映射',
  'keys.mappings.retry': '重试',
  'keys.mappings.empty': '尚未配置映射。添加一条后，客户端就能用该别名调用对应模型。',
  'keys.mappings.column.alias': '别名',
  'keys.mappings.column.target': '目标模型',
  'keys.mappings.column.source': '来源',
  'keys.mappings.source.manual': '手动',
  'keys.mappings.source.automatic': '自动',
  'keys.mappings.automatic_managed': '由命名空间管理',
  'keys.mappings.readonly': '你可以查看映射和冲突，但当前角色无权修改。',
  'keys.mappings.namespace.title': '命名空间操作',
  'keys.mappings.namespace.description':
    '一次选择一个命名空间。「映射当前模型」只执行一次；「启用自动映射」还会自动纳入后续新增模型。两者都会去掉“命名空间/”前缀作为别名。',
  'keys.mappings.namespace.select_label': '命名空间',
  'keys.mappings.namespace.select_placeholder': '选择命名空间',
  'keys.mappings.namespace.selected_hint': '该命名空间当前可见 {count} 个模型。',
  'keys.mappings.namespace.apply': '映射当前模型',
  'keys.mappings.namespace.auto_enable': '启用自动映射',
  'keys.mappings.namespace.auto_enabled_button': '自动映射已启用',
  'keys.mappings.namespace.operations_disabled':
    '映射升级期间暂时无法映射当前模型或启用新的自动命名空间；手动映射和停用已有自动命名空间仍可使用。',
  'keys.mappings.namespace.enabled_title': '已自动映射的命名空间',
  'keys.mappings.namespace.enabled_empty': '尚未启用任何命名空间的自动映射。',
  'keys.mappings.namespace.auto_disable': '停用',
  'keys.mappings.namespace.applied': '已映射 {namespace} 当前的 {count} 个模型。',
  'keys.mappings.namespace.auto_enabled': '已为 {namespace} 启用自动映射。',
  'keys.mappings.namespace.auto_disabled': '已停用 {namespace} 的自动映射。',
  'keys.mappings.namespace.disable_title': '停用自动映射？',
  'keys.mappings.namespace.disable_description':
    '停用后，{namespace} 将不再自动提供别名；同时存在手动映射的别名仍会保留。',
  'keys.mappings.namespace.disable_confirm': '停用自动映射',
  'keys.mappings.namespace.disable_cancel': '保持启用',
  'keys.mappings.conflicts.title': '发现重名的短模型名',
  'keys.mappings.conflicts.description':
    '这些别名同时指向多个模型，已从实际路由中排除。停用一个自动命名空间或移除冲突的手动映射；完整的“命名空间/模型”名称仍可使用。',
  'keys.mappings.conflicts.delete_manual': '删除手动映射',
  'keys.mappings.conflicts.delete_title': '删除这条手动映射？',
  'keys.mappings.conflicts.delete_description':
    '将删除别名 {alias} 的手动映射。若剩余自动候选不再重名，该别名会恢复解析。',
  'keys.mappings.conflicts.delete_confirm': '删除手动映射',
  'keys.mappings.conflicts.delete_cancel': '保留映射',
  'keys.mappings.edit': '编辑',
  'keys.mappings.delete': '删除',
  'keys.mappings.add_title': '新增映射',
  'keys.mappings.edit_title': '修改 {alias} 的目标',
  'keys.mappings.alias_label': '别名',
  'keys.mappings.alias_placeholder': 'claude-sonnet-4-5',
  'keys.mappings.alias_hint': '客户端请求 model 字段里发送的名称，不能包含“/”。',
  'keys.mappings.alias_required': '输入别名',
  'keys.mappings.alias_no_slash': '别名不能包含“/”',
  'keys.mappings.sku_label': '目标模型',
  'keys.mappings.sku_placeholder': '选择模型',
  'keys.mappings.sku_required': '选择模型',
  'keys.mappings.submit_add': '添加映射',
  'keys.mappings.submit_save': '保存修改',
  'keys.mappings.cancel_edit': '取消编辑',
  'keys.mappings.toast.added': '已添加映射',
  'keys.mappings.toast.updated': '已更新映射',
  'keys.mappings.toast.deleted': '已删除映射',
  'keys.mappings.catalog_error': '无法加载模型目录，暂时不能新增映射。',
  'keys.plaintext.title': '复制你的 API 密钥',
  'keys.plaintext.warning':
    '这是你唯一一次看到完整密钥。立即复制并妥善保存；如果遗失，只能删除后重新创建。',
  'keys.plaintext.hint.click_to_copy': '点击复制',
  'keys.plaintext.hint.click_with_name': '{name} · 点击复制',
  'keys.plaintext.copied': '已复制到剪贴板',
  'keys.plaintext.copy_anywhere': '点击密钥区域同样可以复制',
  'keys.plaintext.copy_button': '复制密钥',
  'keys.plaintext.done': '已复制，关闭',
  'keys.plaintext.close': '我已保存，关闭',
  'keys.plaintext.copy_aria_label': '复制 API 密钥到剪贴板',
  'keys.revoke_modal.title': '删除这把 API 密钥？',
  'keys.revoke_modal.description.before': '删除密钥 ',
  'keys.revoke_modal.description.after':
    ' 会立即使其失效。仍在使用它的应用会开始收到 401 错误。此操作无法撤销。',
  'keys.revoke_modal.submit': '删除',
  'keys.revoke_modal.submitting': '删除中…',
  'keys.revoke_modal.cancel': '取消',
  'keys.revoke_modal.close': '关闭',
  'keys.revoke_modal.type_label': '输入密钥名称以确认删除',

  // ── Service status page (/status) ──────────────────────────────
  'status.title': '服务状态',
  'status.subtitle': '区分本地配置问题和平台服务的可用性问题。',
  'status.error': '无法加载服务状态。',
  'status.retry': '重试',
  'status.kpi.overall': '总体状态',
  'status.kpi.overall_hint': '{healthy}/{total} 服务正常',
  'status.kpi.health': '健康检查',
  'status.kpi.health_hint': '平台 API 探测',
  'status.health.ok': '正常',
  'status.health.down': '异常',
  'status.kpi.updated': '更新时间',
  'status.kpi.updated_hint': '状态数据最近一次刷新',
  'status.state.healthy': '正常',
  'status.state.busy': '繁忙',
  'status.state.unavailable': '不可用',
  'status.timeline.caption': '服务可用性',
  'status.timeline.window': '最近 {hours} 小时',
  'status.timeline.uptime': '可用率',
  'status.timeline.window_start': '{hours} 小时前',
  'status.timeline.window_now': '现在',
  'status.timeline.block_title': '{start}–{end} · 成功率 {rate}%',
  'status.timeline.block_no_data': '{start}–{end} · 无请求',
  'status.timeline.skus_show': '展开 {count} 个模型明细',
  'status.timeline.skus_hide': '收起模型明细',
  'status.events.caption': '最近事件',
  'status.events.empty': '暂无状态变化事件。',
  'status.empty.title': '暂无可用性数据',
  'status.empty.hint': '当前还没有可展示的服务时间线，稍后再回来查看。',
  'status.capacity.bucket_suffix': '满载拒绝率 {ratio}%',
  'status.capacity.legend': 'Codex 行按满载拒绝率着色：<15% 绿、15–30% 黄、≥30% 红',
  'status.official.caption': '官方服务状态',
  'status.official.hint': '数据来自 OpenAI 与 Anthropic 官方状态页，每分钟同步',
  'status.official.checked_at': '检查于 {time}',
  'status.official.view_page': '查看官方状态页',
  'status.official.stale': '官方状态暂时无法获取，显示为未知',
  'status.official.no_incidents': '过去 24 小时没有官方事故',
  'status.official.all_operational': '全部组件正常',
  'status.official.components_show': '展开全部 {count} 个组件',
  'status.official.components_hide': '收起全部 {count} 个组件',
  'status.official.resolved': '已解决',
  'status.official.started_at': '开始于 {time}',
  'status.official.scheduled': '计划时段 {start}–{end}',
  'status.official.latest_update': '最新更新 {time}',
  'status.official.expand': '展开全部',
  'status.official.collapse': '收起',
  'status.official.indicator.none': '运行正常',
  'status.official.indicator.minor': '轻微故障',
  'status.official.indicator.major': '严重故障',
  'status.official.indicator.critical': '重大故障',
  'status.official.indicator.maintenance': '维护中',
  'status.official.indicator.unknown': '未知',
  'status.official.component.operational': '正常',
  'status.official.component.degraded_performance': '性能下降',
  'status.official.component.partial_outage': '部分中断',
  'status.official.component.major_outage': '严重中断',
  'status.official.component.under_maintenance': '维护中',
  'status.official.incident.investigating': '调查中',
  'status.official.incident.identified': '已定位',
  'status.official.incident.monitoring': '观察中',
  'status.official.incident.resolved': '已解决',
  'status.official.incident.postmortem': '复盘中',
  'status.official.maintenance.scheduled': '已排期',
  'status.official.maintenance.in_progress': '进行中',
  'status.official.maintenance.verifying': '验证中',
  'status.official.maintenance.completed': '已完成',
  'status.official.impact.none': '无影响',
  'status.official.impact.minor': '轻微',
  'status.official.impact.major': '严重',
  'status.official.impact.critical': '重大',

  // ── Integration docs page (/docs) ──────────────────────────────
  'docs.title': '接入文档',
  'docs.subtitle': '从创建密钥、认识模型名与映射，到 Claude Code、Codex 和各图形客户端的接入步骤。',
  'docs.toast.copied': '已复制',
  'docs.copy': '复制',
  'docs.toc.aria': '接入文档目录',
  'docs.fact.anthropic.title': 'Anthropic Base URL',
  'docs.fact.anthropic.note': '不带 /v1',
  'docs.fact.openai.title': 'OpenAI Base URL',
  'docs.fact.openai.note': '必须带 /v1',
  'docs.fact.auth.title': '认证方式',
  'docs.fact.auth.note': '完整密钥只显示一次',
  'docs.fact.copy': '复制',
  'docs.section.prepare.label': '准备',
  'docs.section.prepare.title': '接入前准备',
  'docs.section.models.label': '模型名',
  'docs.section.models.title': '模型名与映射',
  'docs.section.createKey.label': '创建密钥',
  'docs.section.createKey.title': '创建 API 密钥',
  'docs.section.claudeCode.label': 'Claude Code',
  'docs.section.claudeCode.title': 'Claude Code 接入',
  'docs.section.codex.label': 'Codex',
  'docs.section.codex.title': 'Codex 接入',
  'docs.section.ccSwitch.label': 'CC Switch',
  'docs.section.ccSwitch.title': 'CC Switch 接入',
  'docs.section.openclaw.label': 'OpenClaw',
  'docs.section.openclaw.title': 'OpenClaw 接入',
  'docs.section.clients.label': '图形客户端',
  'docs.section.clients.title': '图形客户端接入',
  'docs.section.claudeDesktop.label': 'Claude Desktop',
  'docs.section.claudeDesktop.title': 'Claude Desktop 接入',
  'docs.section.vscode.label': 'VS Code',
  'docs.section.vscode.title': '在 VS Code 中使用',
  'docs.section.images.label': 'GPT 图片',
  'docs.section.images.title': 'GPT 图片生成与编辑',
  'docs.images.note':
    '标准 OpenAI Images 协议：`POST /v1/images/generations` 图片生成，`POST /v1/images/edits` 图片编辑（支持 multipart 上传）。' +
    '官方 SDK 直接可用，只需把 base_url 指到上面的 OpenAI 兼容地址；模型填目录里的图片 SKU 全名。' +
    '生成远慢于文本请求，SDK 的默认超时通常不够，需显式调长。',
  'docs.images.sdk': 'Python 官方 SDK（图片生成 + 图片编辑）',
  'docs.images.curl': 'curl（图片生成 + 图片编辑）',
  'docs.images.table.generate': 'POST /v1/images/generations 参数',
  'docs.images.table.edit': 'POST /v1/images/edits 参数',
  'docs.images.column.param': '参数',
  'docs.images.column.required': '必填',
  'docs.images.column.default': '默认值',
  'docs.images.column.behaviour': '实际行为',
  'docs.images.required.yes': '是',
  'docs.images.required.no': '否',
  'docs.images.default.none': '无',
  'docs.images.param.model':
    '目录里图片 SKU 的完整名称（`命名空间/模型名`）。裸名只在该密钥配过模型映射时可用。',
  'docs.images.param.prompt': '要生成或修改的画面描述。',
  'docs.images.param.quality': '接受但不生效。响应里的 `quality` 报告实际档位。',
  'docs.images.param.n': '仅支持 1。填其他值会返回 400，不会产生任何费用。',
  'docs.images.param.response_format':
    '仅支持 `b64_json`。`url` 会返回 400——本路由不提供托管链接。',
  'docs.images.param.background': '仅支持 `opaque`。`transparent` 会返回 400。',
  'docs.images.param.size':
    '作为构图比例的提示传递，不是硬约束。响应里的 `size` 是实际产出的尺寸，与请求值可能不同。以响应为准，不要假设等于请求值。',
  'docs.images.param.output_format': '支持 `png` / `jpeg` / `webp`。',
  'docs.images.param.output_compression': '压缩质量，仅对 `jpeg` / `webp` 生效。',
  'docs.images.param.image':
    '要修改的图片，可传多张参考图。支持 multipart 文件上传，也支持 JSON 里的 base64。单次请求体上限 12 MB。',
  'docs.images.param.mask': '遮罩图，标出需要重绘的区域。遮罩是提示而非精确裁剪。',
  'docs.images.billing':
    '计费按响应 `usage` 结算。`output_tokens_details.image_tokens` 是图片出量，`input_tokens` 是输入量，两者都按该图片 SKU 的费率计价——用量与账单页可逐条核对。',
  'docs.section.videos.label': 'Grok 视频',
  'docs.section.videos.title': 'Grok 视频生成',
  'docs.videos.note':
    'xAI 官方视频线：`POST /v1/videos/generations` 提交任务，`GET /v1/videos/{request_id}` 查询状态。' +
    '模型填目录里的视频 SKU 全名（`命名空间/模型名`），不要写官方裸名。' +
    'Python `xai-sdk` 的 `api_host` 填的是不带 `/v1` 的 gateway origin（与上面的 Anthropic 入口同一段）；SDK 的 host 参数名若与上游不一致，以能打到 `POST /v1/videos/generations` 为准。' +
    '提交成功只回 `{ "request_id": "…" }`，这是本网关的包装 id，响应里不会出现上游 `vid_`。' +
    '`done` 时的 `video.url` 是临时 mp4 地址，要留下就立刻下载。' +
    '生成远慢于文本请求，SDK 的默认超时通常不够，需显式调长。',
  'docs.videos.sdk': 'Python xai-sdk（提交；SDK 自行轮询）',
  'docs.videos.curl': 'curl（提交 + 轮询）',
  'docs.videos.table.generate': 'POST /v1/videos/generations 参数',
  'docs.videos.column.param': '参数',
  'docs.videos.column.required': '必填',
  'docs.videos.column.default': '默认值',
  'docs.videos.column.behaviour': '实际行为',
  'docs.videos.required.yes': '是',
  'docs.videos.required.no': '否',
  'docs.videos.default.none': '无',
  'docs.videos.param.model':
    '目录里的视频 SKU 全名（`xai/grok-imagine-video` 或 `xai/grok-imagine-video-1.5`）。裸名只在该密钥配过映射时可用。',
  'docs.videos.param.prompt': '文生视频描述。空字符串返回 400，不扣费。',
  'docs.videos.param.resolution':
    '只接受 `480p` / `720p`。省略返回 400。`1080p` 返回 400「This resolution is not available.」，不扣费。其它值同样 400。',
  'docs.videos.param.duration':
    '正整数秒，支持范围 1–15。省略时走上游默认 8 秒。非正整数返回 400，不扣费。',
  'docs.videos.param.image':
    '图生视频未接。出现 `image`、`image_url` 或 `images` 字段会返回 400，不扣费。',
  'docs.videos.param.reference_images':
    '已接通并计费。1–14 张（14 是上限，不承诺多图合成）。每项为 `{"url": "…"}`（`data:` URL 也可）或 ' +
    '`{"data": "<base64>", "media_type": "image/png"}`（`jpeg` / `webp` / `gif` 同样接受）。' +
    '内联图：解码后每张 5 MiB、合计 8 MiB；https URL 不计入。请求体上限 10 MiB。' +
    '每张参考图按该模型的单张价加收（各模型费率不同，见定价页）。超限或形态不合法返回 400，不扣费。',
  'docs.videos.param.reference':
    '未接。出现 `reference_audios` 或 `voices` 字段会返回 400，不扣费。',
  'docs.videos.param.unforwarded': '本轮不转发上游，写了等于没写。',
  'docs.videos.param.idempotency':
    '请求头。省略时由网关自行生成。同一 key 配不同 body 会返回 409。',
  'docs.videos.poll':
    '`GET /v1/videos/{id}` 的 `status`：`pending` / `done` / `failed` / `expired`。' +
    '`done` 时带 `video.url`（临时 mp4，立刻下载）和 `video.duration`（终态秒数；账单按这个，不按请求里的 duration）。' +
    '同一账号同一分辨率档同时只允许 1 个在途任务，超出返回 429 `TOO_MANY_TASKS_IN_FLIGHT`。',
  'docs.videos.billing':
    '按两个单位计价：实际上游交付的秒数 × 分辨率档每秒价（`480p` / `720p`），再加上每张参考图 × 该模型的单张价' +
    '（各模型费率不同，挂牌价见定价页）。没有参考图的任务口径不变。' +
    '没交付（4xx、`failed` / `expired`、成功但没有可用 url 或时长）不扣费。定价页可逐条核对。',
  'docs.section.seedance.label': 'Seedance 视频',
  'docs.section.seedance.title': 'Seedance 视频生成',
  'docs.seedance.note':
    'ark 任务线协议，不是 xAI 那条视频线。提交 `POST /v1/tasks/submit`，请求体为 `{model, input:{content:[…]}, parameters:{duration,resolution,ratio}}`；' +
    '轮询 `GET /v1/tasks/status?task_id=…`。状态词为 `Pending` / `Running` / `Success` / `Failure` / `Expired`（首字母大写）。' +
    '成片在 `output.urls[]`（数组）。没有官方 SDK，下面两张卡分别是 curl 与 Python 标准库。',
  'docs.seedance.curl': 'curl（提交 + 轮询）',
  'docs.seedance.python': 'Python 标准库（提交 + 轮询）',
  'docs.seedance.table.params': 'POST /v1/tasks/submit 参数',
  'docs.seedance.column.param': '参数',
  'docs.seedance.column.required': '必填',
  'docs.seedance.column.default': '默认值',
  'docs.seedance.column.behaviour': '实际行为',
  'docs.seedance.required.yes': '是',
  'docs.seedance.required.no': '否',
  'docs.seedance.default.none': '无',
  'docs.seedance.param.duration':
    '整数秒。seedance-2.0：4–15（没有 1–3 秒）。seedance-2.5：4–30，或 `-1`（智能时长）。' +
    '越界在花钱之前被拒。也可用提示词命令 `--duration` / `--dur`；结构化字段优先，同一命令重复出现取最后一个。',
  'docs.seedance.param.resolution':
    'seedance-2.0 只接受 `480p` / `720p`；`1080p` / `4K` 在路由准入处返回 400，不产生花费。' +
    'seedance-2.5 接受 `480p` / `720p` / `1080p`。越界在花钱之前被拒。' +
    '也可用提示词命令 `--resolution` / `--rs`；结构化字段优先，同一命令重复出现取最后一个。',
  'docs.seedance.param.ratio':
    '字符串。也可用提示词命令 `--ratio` / `--rt`；结构化字段优先，同一命令重复出现取最后一个。' +
    '`parameters` 是封闭词表，只认 `duration` / `resolution` / `ratio` 三个名字；出现其它结构化字段会被明确拒绝，不是静默忽略。',
  'docs.seedance.reference': '参考素材作为 `input.content` 的额外成员，与文本段并列传入。',
  'docs.seedance.reference.image': '参考图（`image_url`）',
  'docs.seedance.reference.video': '参考视频（`video_url`）',
  'docs.seedance.artifacts':
    '`output.urls[]` 是本网关的鉴权句柄，取的时候要带同一个 bearer token，有效期 24 小时，过期只能重新生成。',
  'docs.seedance.billing':
    '按 token 计价，按请求是否含视频输入分档。参考图与纯文生同价；参考视频走另一档（每 token 单价更低，但 token 数约翻倍，单条更贵）。' +
    '档位矩阵见定价页，可逐条核对。',
  'docs.section.verify.label': '验证排查',
  'docs.section.verify.title': '验证与排查',
  'docs.prepare.console': '控制台',
  'docs.prepare.keys_page': '密钥管理',
  'docs.prepare.models': '模型列表',
  'docs.prepare.requests': '请求日志',
  'docs.prepare.warning':
    '最常见的错误是 Base URL 填错：Anthropic 入口不要带 /v1，OpenAI 兼容入口必须带 /v1。',
  'docs.prepare.anthropic_entry': 'Anthropic 入口 · 不带 /v1',
  'docs.prepare.anthropic_fit':
    'Claude 系模型走这里：Claude Code、Claude Desktop 等 Anthropic 协议工具。部分 Claude 命名空间仅接受 Claude Code 客户端。',
  'docs.prepare.openai_entry': 'OpenAI 入口 · 带 /v1',
  'docs.prepare.openai_fit':
    'GPT 系模型走这里：Codex、OpenClaw、Cherry Studio、ChatBox、LobeChat 等 OpenAI 兼容工具。',
  'docs.prepare.env_check': '命令行环境检查',
  'docs.models.intro':
    '模型名有两种写法：带「/」的完整模型名（命名空间/模型，例如 {example}）在任何密钥上直接可用；不带「/」的短名只查当前密钥的模型映射表，没有对应映射会返回 404。',
  'docs.models.row.canonical': '完整模型名',
  'docs.models.row.canonical_value':
    '「命名空间/模型」形式，以「模型定价」页展示的名称为准，复制即可用，推荐所有新接入使用。',
  'docs.models.row.alias': '短名（别名）',
  'docs.models.row.alias_value':
    '给只能发送裸模型名的客户端用。需要先在密钥上配置「别名 → 目标模型」映射，别名不能包含「/」。',
  'docs.models.row.manage': '映射管理',
  'docs.models.row.manage_value':
    '在「密钥」页打开「模型映射」，可以一键映射某个命名空间当前的全部模型，也可以启用自动映射，让后续新增模型自动出现。重名短名会明确告警并暂停解析；停用冲突的自动命名空间或删除冲突的手动映射后即可恢复。',
  'docs.models.row.pricing': '价格差异',
  'docs.models.row.pricing_value':
    '同一个模型可能出现在多个命名空间且倍率不同——选模型名同时也是在选价格，详见「模型定价」页。',
  'docs.models.list_snippet': '查询当前密钥可调用的模型',
  'docs.models.source_of_truth':
    '每把密钥能调用什么，以 GET /v1/models 的返回为准：它列出你可见的全部完整模型名，以及这把密钥上已配置的别名。',
  'docs.models.cc_lane':
    '部分 Claude 命名空间仅接受 Claude Code 客户端发出的请求，用 curl 或图形客户端调用会返回上游错误。这类模型在 Claude Code 里使用，或改选支持通用 API 调用的命名空间。',
  'docs.createKey.step1.title': '打开密钥页',
  'docs.createKey.step1.body': '登录控制台后进入「密钥」页面，点击创建密钥。',
  'docs.createKey.step2.title': '按需预置映射和限额',
  'docs.createKey.step2.body':
    '密钥不区分供应商。可选择一个命名空间，由服务端一次性预置当前模型的短名映射；如需后续新模型自动出现，可在创建后启用自动映射。也可设置消费限额。',
  'docs.createKey.step3.title': '复制并保存',
  'docs.createKey.step3.body': '完整密钥只显示一次。建议按工具命名，便于追踪用量。',
  'docs.createKey.row.name': '密钥名称',
  'docs.createKey.row.name_value':
    '建议按工具命名，例如 claude-code-mac、codex-work、cherry-studio。',
  'docs.createKey.row.template': '命名空间模板',
  'docs.createKey.row.template_value':
    '可选。由服务端一次性为所选命名空间当前的每个模型预置「别名 = 模型名」映射。在「模型映射」中启用自动模式后，新模型也会自动同步；若出现重名会先告警，避免路由歧义。',
  'docs.createKey.row.limits': '消费限额',
  'docs.createKey.row.limits_value':
    '可按日、周、月设置人民币限额。限额按计量后的用量判定：触碰限额的那次请求仍会完成，密钥随后暂停到该周期结束。',
  'docs.createKey.hint':
    '一把密钥即可调用你可见的全部模型（Claude 和 GPT 通用）。建议按工具或成员分别建密钥，便于用量归因。总览页余额为 0 时，先联系团队所有者或管理员充值。',
  'docs.createKey.open_keys': '去创建密钥',
  'docs.platform.aria': '操作系统',
  'docs.platform.macos': 'macOS / Linux',
  'docs.platform.windows': 'Windows',
  'docs.claudeCode.intro':
    'Claude Code 适合在终端里处理代码阅读、修改、重构和项目级任务。接入 Nxtpath 时使用 Anthropic 入口，并把模型设置为完整模型名（命名空间/模型）。',
  'docs.claudeCode.install': '安装并确认版本',
  'docs.claudeCode.temp_env': '临时环境变量',
  'docs.claudeCode.long_term': '长期生效配置',
  'docs.claudeCode.row.model': '模型设置',
  'docs.claudeCode.row.model_value':
    'ANTHROPIC_MODEL 填「模型定价」页的完整模型名；后台小模型可选设置 ANTHROPIC_SMALL_FAST_MODEL。若不设置，Claude Code 发送的默认模型名需要在密钥上有同名映射。',
  'docs.claudeCode.row.first_test': '第一次测试',
  'docs.claudeCode.row.first_test_value': '进入一个小项目目录，让 Claude Code 先执行只读任务。',
  'docs.claudeCode.row.prompt': '推荐提示',
  'docs.claudeCode.row.prompt_value': '先不要修改文件，阅读项目结构，告诉我这个项目的启动方式。',
  'docs.claudeCode.row.url_mistake': '常见地址错误',
  'docs.claudeCode.row.url_mistake_value': 'ANTHROPIC_BASE_URL 不要误加 /v1。',
  'docs.claudeCode.disable_betas': '关闭实验 beta 字段',
  'docs.codex.intro':
    'Codex 类工具使用 OpenAI 兼容接口。接入 Nxtpath 时，Base URL 填 OpenAI 入口，API 密钥用你的 Nxtpath 密钥，模型填完整模型名（例如 {example}）。config.toml 中两处密钥必须相同；修改后需完全退出 Codex / ChatGPT 桌面端（含后台进程）再重新打开才会生效。',
  'docs.codex.env.codex': 'Codex 环境变量',
  'docs.codex.row.provider': 'Provider',
  'docs.codex.row.provider_value': 'OpenAI Compatible / Custom OpenAI',
  'docs.codex.row.base_url': 'Base URL / API Host',
  // Field labels reproduced verbatim from the tool's own UI — a reader is
  // matching these against what is on their screen, so the 「API 密钥」
  // terminology rule (file header) does not apply to them.
  'docs.codex.row.api_key': 'API Key',
  'docs.codex.row.api_key_value': '你的 Nxtpath API 密钥，在「密钥」页复制。',
  'docs.codex.row.model': '模型',
  'docs.ccSwitch.intro':
    'CC Switch 用来统一管理 Claude Code、Codex、OpenClaw 等工具的 Provider。接入后可以在本地切换 Nxtpath Claude 和 OpenAI Compatible 配置。',
  'docs.ccSwitch.install': 'macOS 安装',
  'docs.ccSwitch.claude_panel': 'Claude Code Provider',
  'docs.ccSwitch.openai_panel': 'Codex / OpenAI Provider',
  'docs.ccSwitch.row.provider_name': 'Provider 名称',
  'docs.ccSwitch.row.claude_provider_name': 'Nxtpath Claude',
  'docs.ccSwitch.row.openai_provider_name': 'Nxtpath OpenAI Compatible',
  'docs.ccSwitch.row.app': '工具 / App',
  'docs.ccSwitch.row.claude_app': 'Claude Code',
  'docs.ccSwitch.row.openai_app': 'Codex / OpenClaw / OpenAI Compatible',
  'docs.ccSwitch.row.protocol': 'API 协议',
  'docs.ccSwitch.row.claude_protocol': 'Anthropic Compatible',
  'docs.ccSwitch.row.openai_protocol': 'OpenAI Compatible',
  'docs.ccSwitch.row.base_url': 'Base URL',
  // Field labels reproduced verbatim from the tool's own UI — a reader is
  // matching these against what is on their screen, so the 「API 密钥」
  // terminology rule (file header) does not apply to them.
  'docs.ccSwitch.row.api_key': 'API Key',
  'docs.ccSwitch.row.claude_key': '你的 Nxtpath API 密钥',
  'docs.ccSwitch.row.openai_key': '你的 Nxtpath API 密钥（可与 Claude 共用一把）',
  'docs.ccSwitch.row.model': '模型',
  'docs.ccSwitch.checklist.title': '切换后没有生效时检查',
  'docs.ccSwitch.checklist.1': 'CC Switch 当前选中的 Provider 是否正确。',
  'docs.ccSwitch.checklist.2': '终端是否是在切换之前打开的；切换后要开新终端。',
  'docs.ccSwitch.checklist.3': 'Claude Code / Codex 是否需要重启。',
  'docs.ccSwitch.checklist.4': 'Base URL 是否对应协议：Anthropic 不带 /v1，OpenAI 带 /v1。',
  'docs.ccSwitch.checklist.5': '控制台请求日志是否出现新的调用记录。',
  'docs.openclaw.intro':
    'OpenClaw 按 OpenAI 兼容接口配置。把 Provider 指向 Nxtpath OpenAI Base URL，模型填完整模型名。',
  'docs.openclaw.snippet': 'OpenAI 兼容 Provider',
  'docs.openclaw.row.base_url': 'Base URL',
  // Field labels reproduced verbatim from the tool's own UI — a reader is
  // matching these against what is on their screen, so the 「API 密钥」
  // terminology rule (file header) does not apply to them.
  'docs.openclaw.row.api_key': 'API Key',
  'docs.openclaw.row.api_key_value': '你的 Nxtpath API 密钥。',
  'docs.openclaw.row.model': '模型',
  'docs.openclaw.row.verify': '验证提示',
  'docs.openclaw.row.verify_value': '回复：OpenClaw 已接入。',
  'docs.clients.column.tool': '工具',
  'docs.clients.column.vendor': '协议',
  'docs.clients.column.base': 'Base URL',
  'docs.clients.column.notes': '说明',
  'docs.clients.base_both': '双协议',
  'docs.clients.tool.claude_code': '终端 AI 编码，使用 Anthropic 入口。',
  'docs.clients.tool.codex': 'OpenAI 兼容 CLI，推荐 responses wire API。',
  'docs.clients.tool.cc_switch': '统一管理 Claude Code、Codex、OpenClaw 等 Provider。',
  'docs.clients.tool.openclaw': '按 OpenAI Compatible Provider 配置。',
  'docs.clients.tool.cherry_studio':
    '桌面图形客户端；选择支持通用 API 调用的模型（部分 Claude 命名空间仅限 Claude Code）。',
  'docs.clients.tool.chatbox': '轻量聊天客户端，API Host 填 OpenAI 兼容地址。',
  'docs.clients.tool.lobechat': '自部署或图形界面均使用 OpenAI 兼容入口。',
  'docs.clients.tool.claude_desktop': '第三方网关入口；如调用报上游错误，改用 Claude Code。',
  'docs.clients.key_name': '建议 Key 名称：{name}',
  'docs.clients.row.entry': '入口',
  'docs.clients.row.provider_type': '服务商类型',
  'docs.clients.row.provider_type_value': 'OpenAI Compatible / 自定义 OpenAI',
  'docs.clients.row.api_host': 'API 地址',
  'docs.clients.row.model': '模型',
  'docs.clients.row.test': '测试',
  'docs.clients.cherry.entry': '模型服务 / 服务商管理',
  'docs.clients.cherry.test': '用一句话说明你已经连接成功。',
  'docs.clients.cherry.note':
    '部分 Claude 命名空间仅接受 Claude Code 客户端，在 Cherry Studio 里配置会报上游错误；模型名使用「模型定价」页的完整名称。',
  'docs.clients.chatbox.entry': '模型提供方 / API 设置',
  'docs.clients.chatbox.test': '回复：ChatBox 配置成功。',
  'docs.clients.chatbox.note': 'API Host 填完整 OpenAI Base URL，避免漏掉或重复填写 /v1。',
  'docs.clients.lobechat.entry': 'OpenAI / OpenAI Compatible',
  'docs.clients.lobechat.test': '回复：LobeChat 配置成功。',
  'docs.clients.lobechat.note': '团队使用时建议按成员或项目分开创建 Key，排查消耗更清楚。',
  'docs.clients.lobechat_env': 'LobeChat 自部署环境变量',
  'docs.claudeDesktop.intro':
    'Claude Desktop 这里配置的是图形界面的第三方网关，不是 Claude Code 终端环境变量。如果调用报上游错误，说明所选模型仅支持 Claude Code，改用 Claude Code 接入。',
  'docs.claudeDesktop.macos.title': 'macOS 配置流程',
  'docs.claudeDesktop.macos.1': '打开 Help → Troubleshooting → Enable Developer Mode。',
  'docs.claudeDesktop.macos.2': '应用重启后，在欢迎界面保持 Claude Desktop 选中。',
  'docs.claudeDesktop.macos.3': '打开 Developer → Configure Provider。',
  'docs.claudeDesktop.macos.4':
    'Gateway base URL 填 {baseUrl}，Gateway API key 填你的 Nxtpath API 密钥。',
  'docs.claudeDesktop.macos.5':
    '点击 Apply locally，选择 Relaunch now，重启后 Continue with gateway。',
  'docs.claudeDesktop.windows.title': 'Windows 配置流程',
  'docs.claudeDesktop.windows.1': '停留在欢迎页或邮箱输入页。',
  'docs.claudeDesktop.windows.2': '按 Tab 切换到左上角菜单图标，按 Enter 打开菜单。',
  'docs.claudeDesktop.windows.3':
    '选择 Developer → Configure third-party interface 或 Configure Provider。',
  'docs.claudeDesktop.windows.4': 'Gateway base URL 填 {baseUrl}，不要带 /v1。',
  'docs.claudeDesktop.windows.5': 'Apply locally 后按提示重启。',
  'docs.claudeDesktop.row.no_menu': '找不到 Developer 菜单',
  'docs.claudeDesktop.row.no_menu_value':
    '先启用 Developer Mode，并确认当前选中的是 Claude Desktop。',
  'docs.claudeDesktop.row.no_gateway': 'Continue with gateway 没出现',
  'docs.claudeDesktop.row.no_gateway_value': '确认点过 Apply locally 并 Relaunch。',
  'docs.claudeDesktop.row.no_connect': '配置后无法连接',
  'docs.claudeDesktop.row.no_connect_value': '检查 Gateway base URL 和 Key 是否完整。',
  'docs.claudeDesktop.row.rejected': '请求报上游错误',
  'docs.claudeDesktop.row.rejected_value':
    '所选模型所在命名空间仅接受 Claude Code 客户端。改用 Claude Code，或换支持通用客户端的模型。',
  'docs.vscode.intro':
    'VS Code 本身不是模型客户端，但可以作为项目入口，在内置终端里运行 Claude Code 或 Codex。如果插件支持自定义 Provider，也按对应协议填写。',
  'docs.vscode.snippet': 'VS Code 内置终端示例',
  'docs.vscode.row.openai_base': 'OpenAI 兼容 Base URL',
  'docs.vscode.row.anthropic_base': 'Anthropic 兼容 Base URL',
  'docs.vscode.row.openai_models': 'OpenAI 模型',
  'docs.vscode.row.openai_models_value': '完整模型名（命名空间/模型），以「模型定价」页为准。',
  'docs.vscode.row.anthropic_models': 'Anthropic 模型',
  'docs.vscode.row.anthropic_models_value':
    '同样使用完整模型名；部分 Claude 命名空间仅支持 Claude Code，插件调用会报上游错误。',
  'docs.verify.note':
    '建议先用「模型名与映射」章节的 GET /v1/models 验证密钥和可用模型，再发真实请求：把示例里的「命名空间/模型名」换成 /v1/models 返回的名称。部分 Claude 命名空间仅接受 Claude Code 客户端，用 curl 直接调用这类模型会报上游错误，属预期行为。',
  'docs.verify.anthropic_curl': 'Anthropic /v1/messages',
  'docs.verify.openai_curl': 'OpenAI /v1/chat/completions',
  'docs.verify.column.symptom': '现象',
  'docs.verify.column.reason': '可能原因',
  'docs.verify.column.action': '处理方式',
  'docs.verify.error1.symptom': '401 / 未授权',
  'docs.verify.error1.reason': 'Key 填错、复制带空格，或 Key 已被撤销。',
  'docs.verify.error1.action': '重新核对密钥，必要时撤销后重建。',
  'docs.verify.error2.symptom': '404 模型不存在',
  'docs.verify.error2.reason': '短名没有配置映射、名称拼写错误，或该模型对你不可见。',
  'docs.verify.error2.action':
    '改用「模型定价」页的完整模型名（命名空间/模型）；要用短名先在密钥上加映射；GET /v1/models 可列出这把密钥能调用的全部名称。',
  'docs.verify.error3.symptom': '第三方客户端调 claude 系模型报错',
  'docs.verify.error3.reason': '该模型所在命名空间仅接受 Claude Code 客户端。',
  'docs.verify.error3.action': '改用 Claude Code，或换支持通用 API 的模型（见「模型定价」页）。',
  'docs.verify.error4.symptom': '接口不可用 / 路径 404',
  'docs.verify.error4.reason': 'Base URL 错误。',
  'docs.verify.error4.action': 'Anthropic Base URL 不带 /v1；OpenAI Base URL 带 /v1。',
  'docs.verify.error5.symptom': '控制台没有调用记录',
  'docs.verify.error5.reason': '请求没有经过 Nxtpath。',
  'docs.verify.error5.action': '检查当前生效的 Provider、环境变量或配置文件。',
  'docs.verify.error6.symptom': '402 余额不足',
  'docs.verify.error6.reason': '团队余额为 0。',
  'docs.verify.error6.action': '联系团队所有者或管理员充值。',
  'docs.verify.error7.symptom': '403 预算受限',
  'docs.verify.error7.reason':
    '密钥消费限额或成员月度限额已触发：上一次请求的用量计量后越过限额，密钥被暂停。',
  'docs.verify.error7.action':
    '调高对应限额或等待周期重置，都会自动恢复（调高后约十分钟内生效）；密钥限额可在「密钥」页修改。',
  'docs.verify.link.usage': '使用情况',
  'docs.verify.link.usage_detail': '按密钥 / 模型查看消耗',
  'docs.verify.link.requests': '请求日志',
  'docs.verify.link.requests_detail': '查看每次请求和错误',
  'docs.verify.link.pricing': '模型定价',
  'docs.verify.link.pricing_detail': '确认当前可用模型',
  'docs.verify.security':
    'API 密钥只在自己的工具里使用，不要发给别人，不要贴到公开仓库、截图或群聊。每个工具单独创建密钥，泄露时只需撤销重建对应那把。',
  'docs.served.loading': '正在加载最新接入文档…',
  'docs.served.error': '最新接入文档加载失败，正在显示内置副本。',

  // ── Usage page ─────────────────────────────────────────────────
  'usage.range.aria_label': '时间范围',
  'usage.range.today': '今天',
  'usage.range.7d': '7 天',
  'usage.range.30d': '30 天',
  'usage.range.90d': '90 天',
  'usage.range.prev_month': '上个月',
  'usage.range.next_month': '下个月',
  'usage.range.presets_aria_label': '预设范围',
  'usage.summary.requests': '请求数',
  'usage.summary.cost': '费用',
  'usage.summary.loading': '加载中',
  'usage.model_mix.title': '按模型',
  'usage.summary.tokens_in': '输入 Token',
  'usage.summary.tokens_out': '输出 Token',
  'usage.summary.cache_write': '缓存写入',
  'usage.summary.cache_read': '缓存读取',
  'usage.summary.blended_spend': '综合费用',
  'usage.summary.spend': '费用',
  'usage.summary.cache_hit': '缓存命中',
  'usage.summary.tokens_value': '{tokens} Token',
  'usage.requests_count': '{count} 次请求',
  'usage.succeeded_count': '{count} 次成功',
  'usage.pagination.previous': '上一页',
  'usage.pagination.next': '下一页',
  'usage.metric.total': '全部',
  'usage.metric.in': '输入',
  'usage.metric.out': '输出',
  'usage.metric.write': '缓存写入',
  'usage.metric.read': '缓存读取',
  'usage.error.load': '无法加载用量数据。',
  'usage.retry': '重试',
  'usage.admin.title': 'Token 使用情况',
  'usage.admin.subtitle': '汇总每位成员、每把密钥、每次请求的模型消耗。',
  'usage.admin.window.meta': '{from} - {to} · 刷新于 {time}',
  'usage.admin.window.bucket_timezone': '时段按 {timezone} 切分',
  'usage.admin.range.7d': '近 7 天',
  'usage.admin.range.30d': '近 30 天',
  'usage.admin.range.90d': '近 90 天',
  'usage.admin.range.month': '本月',
  'usage.admin.filter.model': '模型',
  'usage.admin.filter.api_key': 'API 密钥',
  'usage.admin.filter.department': '组织单元',
  'usage.admin.filter.member': '成员',
  'usage.admin.filter.all_models': '全部模型',
  // #1672 之前的链接还原出的选择：一个模型名，其下全部厂商。写成与服务端选项标签
  // （`模型 · 厂商`）同形，让它读起来是同级的一项，「全部厂商」占着厂商的位置。
  'usage.admin.filter.model_all_vendors': '{model} · 全部厂商',
  'usage.admin.filter.all_keys': '全部密钥',
  'usage.admin.filter.all_departments': '全部组织单元',
  'usage.admin.filter.all_members': '全部成员',
  'usage.admin.filter.show_revoked_keys': '显示已吊销密钥',
  'usage.admin.filter.key_active': '生效中',
  'usage.admin.filter.key_revoked': '已吊销',
  'usage.admin.filter.key_unknown': '未找到',
  'usage.admin.filter.no_active_keys': '暂无生效中的 API 密钥',
  'usage.admin.filter.group_by': '分组维度',
  'usage.admin.group_by.model': '模型',
  'usage.admin.group_by.department': '组织单元',
  'usage.admin.group_by.member': '成员',
  'usage.admin.group_by_value.model': '模型',
  'usage.admin.group_by_value.department': '组织单元',
  'usage.admin.group_by_value.member': '成员',
  'usage.admin.granularity.day': '日',
  'usage.admin.granularity.week': '周',
  'usage.admin.granularity.month': '月',
  'usage.admin.summary.cache_write_detail': '提示词缓存创建',
  'usage.admin.summary.cache_read_detail': '提示词缓存复用',
  'usage.admin.summary.cost_detail': '用量日志费用元数据',
  'usage.admin.summary.unit_billing_detail': '视频秒数、图片张数等按量计费的用量，不计入 Token。',
  // 两个口径，绝不合成一个比值：主值数的是所选窗口内动过的，明细数的是此刻还在册的。
  // 窗口内用过、之后被删除或吊销的仍计入前者，所以前者可以大于后者——写成「x / y」
  // 会把这种正常情况显示成一个看起来不可能的分数。
  'usage.admin.summary.seats_with_usage': '本期有用量席位',
  'usage.admin.summary.seats_roster_detail': '当前在册：{seats} 个席位',
  'usage.admin.summary.keys_with_usage': '本期有用量密钥',
  'usage.admin.summary.keys_roster_detail': '当前在册：{keys} 把未吊销',
  // 只有对着早于 router#1681 的 platform-api 才会出现，那时服务端根本不下发在册
  // 密钥数。与 `usage/calibers.ts` 一并删除。
  'usage.admin.summary.keys_roster_unknown': '当前在册：暂无数据',
  'usage.admin.trend.title': 'Token 使用趋势',
  'usage.admin.trend.subtitle': '按{groupBy}堆叠',
  'usage.admin.trend.hint': '悬停任意柱状可查看单个窗口',
  'usage.admin.trend.total': '合计 · {tokens} Token',
  'usage.admin.trend.incomplete': '时段不完整',
  'usage.admin.trend.incomplete_note': '淡色柱状仅有一部分落在所选窗口内。',
  'usage.admin.empty.window': '此窗口暂无用量。',
  'usage.admin.model_mix.subtitle': '所选窗口内的总 Token 占比。',
  'usage.admin.model_mix.subtitle_capped':
    '所选窗口内的总 Token 占比。前 {count} 项单独列出，其余合并为「其他」。',
  'usage.admin.model_mix.empty': '此窗口暂无模型用量。',
  'usage.admin.model_mix.io': '输入 {input} · 输出 {output} · 缓存写入 {write} · 缓存读取 {read}',
  'usage.admin.model_mix.other': '其他',
  'usage.admin.model_mix.other_models': '另外 {count} 个模型',
  'usage.admin.department_mix.title': '组织单元分布',
  'usage.admin.department_mix.subtitle': '本周期各组织单元消耗的 Token。',
  'usage.admin.department_mix.empty': '此窗口暂无组织单元用量。',
  'usage.admin.department_mix.unassigned': '未归属',
  'usage.admin.service.title': '服务健康',
  'usage.admin.service.subtitle': '基于计费元数据的请求质量。',
  'usage.admin.service.all_green': '全部正常',
  'usage.admin.service.rate_limited': '限流',
  'usage.admin.service.cache_hit_rate': '缓存命中率',
  'usage.admin.service.requests_evaluated': '已评估请求',
  'usage.admin.service.avg_latency': '平均延迟',
  'usage.admin.service.recovery_events': '恢复事件',
  'usage.admin.seat.title': '席位用量',
  'usage.admin.seat.subtitle': '{count} 位成员 · 按总 Token 排序',
  'usage.admin.seat.search_placeholder': '搜索姓名、邮箱、组织单元...',
  'usage.admin.seat.search_label': '搜索席位',
  'usage.admin.seat.empty': '此窗口没有匹配的成员用量。',
  'usage.admin.seat.column.member': '成员',
  'usage.admin.seat.column.team': '组织单元',
  'usage.admin.seat.column.primary_model': '主要模型',
  'usage.admin.seat.column.avg_per_request': '平均 / 请求',
  'usage.admin.seat.column.monthly_cap': '月度上限',
  'usage.admin.seat.column.trend_14d': '14 天趋势',
  'usage.admin.seat.column.cost_scoped': '费用 · 所选区间',
  'usage.admin.seat.column.monthly_scoped': '月度上限 · 自然月',
  'usage.admin.seat.group.identity': '席位',
  'usage.admin.seat.group.range': '所选区间',
  'usage.admin.seat.group.month': '自然月',
  'usage.admin.seat.group.trend': '最近 14 天',
  'usage.admin.seat.monthly.no_limit': '未设月度上限',
  'usage.admin.seat.monthly.window_note':
    '月度上限对应 {from} 至 {to}（{zone}）的当月累计，其余各列均按所选区间统计。',
  'usage.admin.seat.status.disabled': '已停用',
  'usage.admin.seat.status.deleted': '已移除',
  'usage.admin.seat.range': '显示第 {from}-{to} 条，共 {total} 条',
  'usage.admin.seat.pagination_aria': '席位分页',
  'usage.member.title': '我的 Token 使用情况',
  'usage.member.subtitle': '查看你自己的用量、预算元数据、API 密钥和请求计费日志。',
  'usage.member.summary.scope_detail': '{range} 个人范围',
  'usage.member.summary.cache_write_detail': '{range} 缓存创建',
  'usage.member.summary.cache_read_detail': '{range} 缓存复用',
  'usage.member.summary.spend_detail': '来自计费元数据',
  'usage.member.summary.cache_hit_detail': '{rate} Token 命中 · {count} 次成功',
  'usage.member.trend.title': '个人 Token 趋势',
  'usage.member.trend.subtitle': '来自你自己密钥的输入和输出 Token。',
  'usage.member.keys.title': '我的 API 密钥',
  'usage.member.keys.subtitle': '由你自己的密钥产生的用量。',
  'usage.member.keys.empty': '此窗口暂无密钥用量。',
  'usage.member.keys.activity': '{ok} 次成功 · {hits} 次缓存命中',
  'usage.member.keys.cache_rates': '请求 {request} · Token {token} · 读写比 {ratio}',
  'usage.member.requests.title': '最近请求',
  'usage.member.requests.subtitle': '仅计费元数据，不收集提示词和响应内容。',
  'usage.member.requests.empty': '此窗口暂无请求元数据。',
  'usage.member.requests.status': '状态 {code}',

  // ── Organization page ──────────────────────────────────────────
  'organization.title': '组织',
  'organization.subtitle': '把成员分配到组织单元，用量视图优先按该分配聚合。',
  'organization.forbidden': '只有所有者和管理员可以查看组织管理。',
  'organization.loading': '正在加载组织数据…',
  'organization.refresh': '刷新',
  'organization.retry': '重试',
  'organization.tab.members': '成员',
  'organization.tab.structure': '结构',
  'organization.tab.permissions': '角色与权限',
  'organization.tab.model_permissions': '模型权限',
  'organization.tab.invitations': '邀请',
  'organization.tab.governance': '治理',
  'organization.tab.audit': '审计',
  'organization.summary.members': '成员数',
  'organization.summary.units': '组织单元',
  'organization.summary.unassigned': '未分配',
  'organization.summary.pending': '待接受邀请',
  'organization.filter.search_placeholder': '搜索姓名、邮箱或组织单元',
  'organization.filter.all_units': '全部组织单元',
  'organization.filter.unassigned': '未分配',
  'organization.column.member': '成员',
  'organization.column.unit': '组织单元',
  'organization.column.role': '角色',
  'organization.column.access': '月度额度',
  'organization.column.permissions': '模型权限',
  'organization.column.keys': 'API 密钥',
  'organization.column.joined': '加入时间',
  'organization.column.status': '状态',
  'organization.role.owner': '所有者',
  'organization.role.admin': '管理员',
  'organization.role.member': '成员',
  'organization.role.finance': '财务',
  'organization.access.unrestricted': '不受限',
  'organization.access.limited': '有上限',
  'organization.access.amount': '{amount} / 月',
  'organization.action.create': '创建',
  'organization.action.save': '保存',
  'organization.action.cancel': '取消',
  'organization.action.edit': '编辑',
  'organization.action.archive': '归档',
  'organization.error.members': '无法加载成员',
  'organization.error.units': '无法加载组织单元',
  'organization.error.invitations': '无法加载邀请',
  'organization.error.keys': '无法加载 API 密钥',
  'organization.members.empty': '暂无成员。',
  'organization.members.toast.unit_updated': '已调整部门',
  'organization.members.error.unit_update': '无法调整部门',
  'organization.members.toast.role_updated': '已调整角色',
  'organization.members.error.role_update': '无法调整角色',
  'organization.members.role.self_lockout': '没有成员管理权限的角色会把自己锁在门外。',
  'organization.members.toast.disabled': '已停用该成员',
  'organization.members.toast.enabled': '已启用该成员',
  'organization.members.error.disable': '无法停用 {member}',
  'organization.members.error.enable': '无法启用 {member}',
  'organization.members.error.status_cause': '{member}：{detail}',
  'organization.members.status.disabled': '已停用',
  'organization.members.status.disable': '停用',
  'organization.members.status.enable': '启用',
  'organization.members.status.disable_aria': '停用 {member}',
  'organization.members.status.enable_aria': '启用 {member}',
  'organization.members.status.disable_title': '停用成员',
  'organization.members.status.disable_body':
    '停用 {member} 后，该成员的 API 密钥立即无法调用、无法再产生费用，也无法访问本组织。数据保留，可随时启用恢复。',
  'organization.members.status.disable_confirm': '确认停用',
  'organization.members.status.disable_close': '关闭停用对话框',
  'organization.members.limit.set': '设置',
  'organization.members.limit.edit_aria': '设置 {member} 的月度额度',
  'organization.members.limit.title': '月度额度',
  'organization.members.limit.description': '为 {member} 填写每月人民币金额。留空表示不限。',
  'organization.members.limit.label': '金额（人民币）',
  'organization.members.limit.clear': '取消限额',
  'organization.members.limit.toast.updated': '已更新月度额度',
  'organization.members.limit.error.update': '无法更新月度额度',
  'organization.members.limit.error.invalid': '月度额度必须是非负的人民币金额。',
  'organization.members.limit.error.zero':
    '额度不能为 0。留空表示不限；要停止该成员消费，停用该成员。',
  'organization.members.keys.loading': '加载中',
  'organization.members.keys.owner_note': '在 API 密钥页管理',
  'organization.members.keys.manage': '查看 {member} 的 API 密钥',
  'organization.members.keys.dialog_title': '{member} 的 API 密钥',
  'organization.members.keys.dialog_subtitle': '分配给该成员的可用密钥。',
  'organization.members.keys.empty': '该成员暂无可用 API 密钥。',
  'organization.members.keys.column.name': '名称',
  'organization.members.keys.column.prefix': '前缀',
  'organization.members.keys.column.created': '创建时间',
  'organization.members.keys.column.last_used': '最后使用',
  'organization.members.keys.column.actions': '操作',
  'organization.members.keys.never_used': '从未使用',
  'organization.members.keys.close': '关闭',
  'organization.members.keys.revoke': '吊销',
  'organization.members.keys.revoke_title': '吊销 API 密钥',
  'organization.members.keys.revoke_body': '吊销后该密钥立即失效，调用方需要更换新的密钥。',
  'organization.members.keys.revoke_type_label': '输入密钥名称以确认吊销',
  'organization.members.keys.revoke_confirm': '确认吊销',
  'organization.members.keys.revoke_error': '无法吊销 API 密钥',
  'organization.members.keys.toast.revoked': 'API 密钥已吊销',
  'organization.detail.title': '组织单元详情',
  'organization.detail.empty': '选择一个组织单元查看分配情况。',
  'organization.detail.parent': '上级',
  'organization.detail.direct_members': '直属成员',
  'organization.detail.status': '状态',
  'organization.structure.title': '结构',
  'organization.structure.subtitle': '按路径和直属成员数查看组织单元。',
  'organization.structure.empty': '暂无组织单元。',
  'organization.structure.create_title': '新建组织单元',
  'organization.structure.name': '名称',
  'organization.structure.name_placeholder': '研发',
  'organization.structure.parent': '上级',
  'organization.structure.root_parent': '顶层组织单元',
  'organization.structure.name_required': '输入组织单元名称',
  'organization.structure.status.active': '启用',
  'organization.structure.status.archived': '已归档',
  'organization.structure.error.create': '无法创建组织单元',
  'organization.structure.error.save': '无法保存组织单元',
  'organization.structure.error.archive': '无法归档组织单元',
  'organization.structure.toast.created': '组织单元已创建',
  'organization.structure.toast.saved': '组织单元已更新',
  'organization.structure.toast.archived': '组织单元已归档',
  'organization.structure.archive_title': '归档组织单元',
  'organization.structure.archive_body': '归档 {unit} 后，它不能再用于成员分配。',
  'organization.structure.archive_confirm': '确认归档',
  'organization.structure.archive_close': '关闭归档对话框',
  // 每行图标按钮的可访问名。同一页有 N 个「归档」，屏幕阅读器要能
  // 分辨归档的是哪一个，所以带上组织单元路径（DESIGN.md §9）。
  'organization.structure.action.save': '保存 {unit}',
  'organization.structure.action.cancel': '取消编辑 {unit}',
  'organization.structure.action.edit': '编辑 {unit}',
  'organization.structure.action.archive': '归档 {unit}',
  'organization.permissions.members.title': '成员',
  'organization.permissions.members.empty': '暂无成员',
  'organization.permissions.members.empty_hint': '邀请成员后才能配置模型权限。',
  'organization.permissions.units.title': '组织单元',
  'organization.permissions.units.empty': '暂无组织单元',
  'organization.permissions.units.empty_hint': '创建组织单元可为整个团队设置模型权限。',
  'organization.permissions.configure': '配置',
  'organization.permissions.mode.inherit': '继承',
  'organization.permissions.mode.all': '全部允许',
  'organization.permissions.mode.custom': '自定义',
  'organization.roles.title': '角色',
  'organization.roles.subtitle': '内置角色锁定只读。自定义角色只能组合既有权限。',
  'organization.roles.degraded':
    '角色接口尚未接通。正在显示本地兜底的内置角色，自定义角色无法编辑。',
  'organization.roles.builtin': '内置',
  'organization.roles.custom': '自定义',
  'organization.roles.members_count': '{count} 人',
  'organization.roles.create_name': '角色名',
  'organization.roles.create_name_placeholder': '运营',
  'organization.roles.create': '新建',
  'organization.roles.name_required': '输入角色名',
  'organization.roles.rename': '重命名 {name}',
  'organization.roles.delete': '删除 {name}',
  'organization.roles.delete_title': '删除角色',
  'organization.roles.delete_body': '删除 {name}。仍挂有成员的角色需先把成员迁走。',
  'organization.roles.delete_confirm': '确认删除',
  'organization.roles.delete_close': '关闭删除对话框',
  'organization.roles.toast.created': '角色已创建',
  'organization.roles.toast.updated': '角色已更新',
  'organization.roles.toast.deleted': '角色已删除',
  'organization.roles.toast.renamed': '角色已改名',
  'organization.roles.error.create': '无法创建角色',
  'organization.roles.error.save': '无法保存角色',
  'organization.roles.error.delete': '无法删除角色',
  'organization.roles.error.rename': '无法改名',
  'organization.roles.save_permissions': '保存权限',
  'organization.roles.readonly': '内置角色不可改权限。',
  'organization.roles.checklist.title': '权限',
  'organization.roles.group.members': '成员',
  'organization.roles.group.keys': '密钥',
  'organization.roles.group.billing': '余额与账单',
  'organization.roles.group.usage': '使用情况与请求日志',
  'organization.roles.group.audit': '审计',
  'organization.roles.group.reports': '数据导出',
  'organization.roles.perm.members_read': '查看',
  'organization.roles.perm.members_manage': '管理',
  'organization.roles.perm.invitations_manage': '管理邀请',
  'organization.roles.perm.api_keys_read': '查看',
  'organization.roles.perm.api_keys_manage': '管理',
  'organization.roles.perm.billing_read': '查看',
  'organization.roles.perm.billing_manage': '管理',
  'organization.roles.perm.usage_read': '查看',
  'organization.roles.perm.audit_read': '查看',
  'organization.roles.perm.reports_export': '导出',
  'organization.roles.empty_custom': '暂无自定义角色。',
  'organization.invitations.title': '待接受邀请',
  'organization.invitations.subtitle': '仍然有效且未被接受或撤销的邀请。',
  'organization.invitations.empty': '暂无待接受邀请。',
  'organization.invitations.empty_hint': '发出邀请后，它会显示在这里，直到被接受或过期。',
  'organization.invitations.empty_hint_readonly': '所有者和管理员可以在这里发出邀请。',
  'organization.invitations.invite': '邀请成员',
  'organization.invitations.revoke': '撤销',
  'organization.invitations.revoke_title': '撤销邀请',
  'organization.invitations.revoke_body': '撤销后 {email} 无法再通过该邀请链接加入。',
  'organization.invitations.revoke_confirm': '确认撤销',
  'organization.invitations.revoke_close': '关闭撤销对话框',
  'organization.invitations.revoke_error': '无法撤销邀请',
  'organization.invitations.toast.revoked': '邀请已撤销',
  'organization.governance.title': '治理概览',
  'organization.governance.subtitle': '分配覆盖、额度限制和管理范围。',
  'organization.governance.assigned': '已分配成员',
  'organization.governance.limited': '有额度上限的成员',
  'organization.governance.managers': '所有者 / 管理员',
  'organization.audit.title': '审计日志',
  'organization.audit.subtitle': '追溯成员、邀请、模型权限、API 密钥设置和组织结构变更。',
  'organization.audit.filter.caption': '筛选',
  'organization.audit.filter.action_label': '动作',
  'organization.audit.filter.action_placeholder':
    '按操作筛选，例如 member_model_permissions.updated',
  'organization.audit.filter.target_label': '目标账号',
  'organization.audit.filter.target_placeholder': '按目标账号 ID 筛选',
  'organization.audit.filter.clear': '清空',
  'organization.audit.error.load': '无法加载审计日志',
  'organization.audit.retry': '重试',
  'organization.audit.empty': '暂无审计事件。',
  'organization.audit.empty_hint': '成员、邀请、API 密钥、模型权限和组织结构变更会出现在这里。',
  'organization.audit.column.time': '时间',
  'organization.audit.column.actor': '操作者',
  'organization.audit.column.action': '动作',
  'organization.audit.column.target': '目标账号',
  'organization.audit.column.detail': '元数据',
  'organization.audit.actor_system': '系统',
  'organization.audit.pagination.previous': '上一页',
  'organization.audit.pagination.next': '下一页',
  'organization.audit.pagination.aria': '审计日志分页',

  // ── Request logs page ─────────────────────────────────────────
  'requests.title': '请求日志',
  'requests.subtitle': '逐条查看请求状态、模型、Token、缓存和费用。',
  'requests.range.aria_label': '请求日志时间范围',
  'requests.filter.request_id': '请求 ID',
  'requests.filter.request_id_placeholder': '精确搜索请求 ID',
  'requests.filter.key': '密钥',
  'requests.filter.key_all': '我的全部密钥',
  'requests.filter.key_all_org': '组织内的全部密钥',
  'requests.filter.status': '状态',
  'requests.filter.status_all': '全部状态',
  'requests.filter.status_success': '成功',
  'requests.filter.status_error': '错误',
  'requests.filter.submit': '搜索',
  'requests.filter.clear': '清除',
  'requests.loading': '正在加载请求日志…',
  'requests.error': '无法加载请求日志。',
  'requests.retry': '重试',
  'requests.empty.title': '此窗口暂无请求记录',
  'requests.empty.hint':
    '把客户端指向 Nxtpath 并发出第一条请求后，每次调用都会带着 token 数和价格出现在这里。',
  'requests.empty.docs': '查看接入文档',
  'requests.empty.filtered.title': '无匹配结果',
  'requests.empty.filtered.hint': '试着放宽或清除筛选条件。',
  'requests.column.time': '时间',
  'requests.column.request': '请求 ID',
  'requests.column.key': '密钥名称',
  'requests.column.model': '模型',
  'requests.modality.chat': '对话',
  'requests.modality.image': '图片',
  'requests.modality.video': '视频',
  'requests.column.status': '状态',
  'requests.column.duration': '耗时',
  'requests.column.first_byte': '首包',
  'requests.column.input': '输入',
  'requests.column.output': '输出',
  'requests.column.cache_read': '缓存命中',
  'requests.column.cache_write': '缓存写入',
  'requests.column.cost': '费用',
  'requests.cost.pending': '计费中',
  'requests.cost.not_charged.not_delivered': '未收费',
  'requests.cost.not_charged.pricing_review': '复核中',
  'requests.duration.ms': '{value} ms',
  'requests.detail.request_id': '完整请求 ID',
  'requests.detail.copy_request_id': '复制请求 ID',
  'requests.detail.api_key_id': '密钥 ID',
  'requests.detail.protocol': '协议',
  'requests.detail.billing': '计费',
  'requests.detail.billing.billed': '已计费',
  'requests.detail.billing.not_billed': '未计费',
  'requests.detail.billing.pending': '计费中',
  'requests.detail.unit_billing': '按量计费',
  'requests.unit_billing.charged': '{quantity} {unit} · {amount}',
  'requests.unit_billing.unit.call': '次',
  'requests.unit_billing.unit.second': '秒',
  'requests.unit_billing.unit.character': '字符',
  'requests.unit_billing.unit.image': '张',
  'requests.unit_billing.not_charged.not_delivered': '未交付，未收费',
  'requests.unit_billing.not_charged.pricing_review': '计费复核中',
  'requests.unit_billing.pending_breakdown': '按量计费；分项明细暂未在此界面提供。',
  'requests.token_billing.column.dimension': '维度',
  'requests.token_billing.column.quantity': '数量',
  'requests.token_billing.column.list_rate': '单价（挂牌）',
  'requests.token_billing.column.amount': '金额（实收）',
  'requests.token_billing.dimension.input': '输入',
  'requests.token_billing.dimension.output': '输出',
  'requests.token_billing.dimension.cache_read': '缓存读',
  'requests.token_billing.dimension.cache_write': '缓存写',
  'requests.token_billing.dimension.cache_write_5m': '缓存写（5 分钟）',
  'requests.token_billing.dimension.cache_write_1h': '缓存写（1 小时）',
  'requests.token_billing.dimension.reasoning': '思考',
  'requests.token_billing.dimension.image_input': '图片输入',
  'requests.token_billing.multiplier': '成交倍率 ×{multiplier}（含协议价与优惠时段折扣）',
  'requests.surcharges.title': '附加费',
  'requests.detail.error_category': '分类',
  'requests.detail.filter_by_key': '按此密钥筛选',
  'requests.detail.view_key_usage': '查看该密钥用量',
  'requests.status_category.success': '成功',
  'requests.status_category.request_error': '请求错误',
  'requests.status_category.rate_limited': '限流',
  'requests.status_category.service_error': '服务错误',
  'requests.pagination.summary': '{from}–{to} / 共 {total}',
  'requests.pagination.prev': '较新',
  'requests.pagination.next': '较旧',
  'requests.archived.badge': '已归档',
  'requests.archived.detail_placeholder': '归档请求的计费明细未加载或不可用。',
  'requests.archived.page_errors': '本页部分归档请求的计费明细未能加载。',

  // ── Optimization hub ───────────────────────────────────────────
  'optimization.title': '优化中心',
  'optimization.subtitle': '把用量日志、缓存遥测和模型目录变成可执行的成本控制。',
  'optimization.window': '30 天优化窗口',
  'optimization.metric.spend': '30 天费用',
  'optimization.metric.loading': '正在加载用量',
  'optimization.metric.token_hit': '{rate} Token 命中',
  'optimization.metric.cache_ratio': '缓存读 / 写',
  'optimization.metric.read_write_detail': '读 {read} · 写 {write}',
  'optimization.metric.input_volume': '输入量',
  'optimization.metric.output_tokens': '输出 {tokens} Token',
  'optimization.recommendations.title': '优化队列',
  'optimization.recommendations.subtitle': '依据用量日志、缓存遥测和实时模型目录排序。',
  'optimization.recommendations.prefix.title': '稳定提示词前缀',
  'optimization.recommendations.prefix.body_empty':
    '先把系统指令和稳定的仓库上下文放在用户输入之前。',
  'optimization.recommendations.prefix.body':
    '{key} 的 Token 缓存命中为 {hit}，读写比为 {ratio}。把动态内容移到稳定上下文之后。',
  'optimization.recommendations.split.title': '把长输出与分类提示词拆开',
  'optimization.recommendations.split.body':
    '{tokens} 缓存写入 Token 只有在后续调用读取时才回本。把长文本生成与重复的分类调用分开。',
  'optimization.recommendations.model.title': '把日常工作默认切到支持缓存的模型',
  'optimization.recommendations.model.body':
    '{model} 在实时模型目录里公开了提示词缓存价格。把 Opus 级别的工作留给架构设计和评审关卡。',
  'optimization.model_lab.title': '模型选型实验台',
  'optimization.model_lab.subtitle': '支持缓存的模型，按有效输入单价排序。',
  'optimization.model_lab.empty': '当前模型目录里没有支持缓存的模型。',
  'optimization.model_lab.meta': '{window} 上下文 · 缓存读取 {rate}',
  'optimization.model_lab.input_unit': '输入 / 每百万 Token',
  'optimization.model_lab.context_unknown': '未知',
  'optimization.simulator.title': '成本模拟器',
  'optimization.simulator.subtitle': '基于实时模型目录价格和提示词缓存假设。',
  'optimization.simulator.input_tokens': '输入 Token',
  'optimization.simulator.cacheable_prefix': '可缓存前缀',
  'optimization.simulator.cached_percent': '缓存 {percent}%',
  'optimization.simulator.naive': '当前模式',
  'optimization.simulator.cached': '启用缓存',
  'optimization.simulator.savings': '节省',
  'optimization.simulator.no_catalog': '加载模型目录后才能模拟价格。',
  'optimization.simulator.using_model': '按 {model} 当前可见的最优目录价格计算。',
  'optimization.template.title': '上下文清理模板',
  'optimization.template.subtitle': '把低信息量的文件挡在 agent 上下文之外。',
  'optimization.template.group.node': 'Node / 网页',
  'optimization.hotspots.title': '请求热点',
  'optimization.hotspots.subtitle': '最近值得优先排查的高成本调用。',
  'optimization.hotspots.empty': '当前窗口内没有请求日志。',
  'optimization.hotspots.column.request': '请求',
  'optimization.hotspots.column.tokens': 'Token 用量',
  'optimization.hotspots.column.cache': '缓存',
  'optimization.hotspots.cache_cell': '读 {read} / 写 {write}',

  // ── Pricing page ──────────────────────────────────────────────
  'pricing.title': '模型定价',
  // #1706: 目录含按单位计费的模型后，副标题不再断言全表都是每百万 Token 量纲。
  'pricing.subtitle': '按供应商、渠道和模型对比实际价格。',
  'pricing.unit': '每百万 Token · CNY',
  // #1706: 分组内含按单位计费行时的中性徽标；单位行自带量纲（每张/每次…）。
  'pricing.unit.neutral': '价格 · CNY',
  'pricing.loading': '正在加载模型定价…',
  'pricing.error': '无法加载模型定价。',
  'pricing.retry': '重试',
  'pricing.empty': '暂时还没有可显示的模型价格。',
  'pricing.no_results': '没有符合当前筛选条件的模型价格。',
  'pricing.vendor.summary': '{models} 个模型 · {channels} 个渠道',
  'pricing.vendor.no_channels': '这个供应商当前没有可用渠道。',
  'pricing.filter.all_vendors': '全部供应商',
  'pricing.filter.all_channels': '全部渠道',
  'pricing.search.placeholder': '搜索模型…',
  'pricing.column.model': '模型',
  'pricing.cell.unavailable': '不可用',
  'pricing.rate.input': '输入',
  'pricing.rate.output': '输出',
  'pricing.rate.cache_read': '缓存命中',
  'pricing.rate.cache_write': '缓存写入',
  'pricing.rate.cache_write_5m': '缓存写入 5m',
  'pricing.rate.cache_write_1h': '缓存写入 1h',
  'pricing.rate.not_billed': '不计费',
  // #1706 — per-unit price labels for unit-metered SKUs; unit nouns are the
  // same closed set as requests.unit_billing.unit.* (次/秒/字符/张).
  'pricing.rate.unit.call': '每次',
  'pricing.rate.unit.second': '每秒',
  'pricing.rate.unit.character': '每字符',
  'pricing.rate.unit.image': '每张',
  'pricing.rate.unit.pending': '价格待定',
  'pricing.rate.reasoning': '推理',
  // tenant#138 — eighth token-card bucket; omitted on the wire when the
  // SKU has no image-input rate. The ticket tk_59ef52d2831fa277 bill was
  // this row missing from the card.
  'pricing.rate.image_input': '图片输入',
  // Epic #898 P6 (#912) — namespace-grouped SKU market view.
  'pricing.filter.all_namespaces': '全部命名空间',
  'pricing.namespace.summary': '{skus} 个模型',
  'pricing.column.rate': '倍率',
  'pricing.column.prices': '价格',
  'pricing.sku.discount': '折扣',
  // tenant#117 — 自描述计价文档渲染器。档位/轴/单位词全部消费后端下发的
  // 双语标签；这两条只是前端框架文案：包住下发单位词的每单位模板，以及
  // 文档版本（或某个费率成员）比本读取器新时的保守占位——宁可不显示，
  // 不显示错价。
  'pricing.billing.per_unit': '每{unit}',
  'pricing.billing.fallback': '详见价目说明',
  // tenant#138 — catalog billing document extras. Copy is the issue's
  // measured wording (basis hints, stacking note) so a tenant reconciling
  // a bill against this page sees the same arithmetic the kernel used.
  'pricing.billing.prompt_tier.base': '基础',
  'pricing.billing.prompt_tier.long': '长提示 ≥ {n} Token',
  'pricing.billing.prompt_tier.basis.varies_by_ingress':
    '分档阈值按调用端点口径判定：Anthropic 端点按 输入+缓存 判档，OpenAI 端点按 输入（含缓存读）判档',
  'pricing.billing.prompt_tier.basis.input_tokens': '分档阈值按输入 Token 判定（含缓存读）',
  'pricing.billing.prompt_tier.basis.input_plus_cache_tokens': '分档阈值按 输入+缓存读+缓存写 判定',
  'pricing.billing.surcharges': '附加费',
  'pricing.billing.sale_windows': '优惠时段 · {timezone}',
  'pricing.billing.sale_windows.stacking': '命中时段的实际倍率 = 挂牌倍率 × 时段折扣',
  'pricing.billing.sale.day.mon': '周一',
  'pricing.billing.sale.day.tue': '周二',
  'pricing.billing.sale.day.wed': '周三',
  'pricing.billing.sale.day.thu': '周四',
  'pricing.billing.sale.day.fri': '周五',
  'pricing.billing.sale.day.sat': '周六',
  'pricing.billing.sale.day.sun': '周日',

  // ── Billing page ───────────────────────────────────────────────
  'billing.title': '余额与账单',
  'billing.subtitle': '查看余额、充值、兑换记录和账单流水。',
  'billing.balance.title': '当前余额',
  'billing.balance.status.funded': '余额充足',
  'billing.balance.status.zero': '余额不足',
  'billing.balance.status.overdrawn': '透支',
  'credit.limit': '额度',
  'credit.used': '已用',
  'credit.remaining': '剩余',
  'credit.available': '可用',
  'credit.overlimit': '超限',
  'credit.grace': '账期',
  'credit.grace.unset': '未设置',
  'credit.grace.days': '{days} 天',
  'credit.due.current': '本期到期',
  'credit.due.next': '下期到期',
  'credit.stage.normal': '正常',
  'credit.stage.frozen': '授信冻结（不影响使用）',
  'credit.stage.stop_notice': '停服预告',
  'credit.stage.stopped': '已停服',
  'credit.stage.collections': '催收中',
  'credit.service_stopped': '服务已停止',
  'credit.new_frozen': '新增授信已冻结',
  'credit.temp.window': '临时授信 {start} 至 {end}',
  'credit.temp.oneshot': '到期一次性出账',
  'credit.oneshot.open': '有待付的一次性账单',
  'credit.exemption': '豁免至 {date}',
  'credit.residual.title': '未结欠款',
  'credit.residual.debt': '欠款 {amount}',
  'credit.residual.due': '到期 {date}',
  'credit.residual.overdue': '逾期 {days} 天',
  'credit.hero.line': '授信 {limit} · 已用 {used}',
  'credit.hero.due': '{date} 到期',
  'credit.hero.residual': '欠款 {amount} · 逾期 {overdue} 天',
  'credit.rate.locked': '锁定汇率 {rate}',
  'billing.balance.loading': '正在加载余额…',
  'billing.balance.error': '无法加载余额。',
  'billing.balance.updated': '更新于 {timestamp}',
  // Gift-credit visibility. `partial` is the two-gift case: the date
  // belongs to one batch, so the amount shown against it must be that
  // batch alone, not the gift total.
  'billing.gift.included': '含赠送 {amount}',
  'billing.gift.partial': '其中 {amount} {when}',
  'billing.gift.expires_in_days': '{days} 天后到期',
  'billing.gift.expires_in_day': '1 天后到期',
  'billing.gift.expires_today': '今天到期',
  'billing.gift.expired': '已过期，待回收',
  'billing.credits.title': '余额构成',
  'billing.credits.subtitle':
    '按入账批次拆分。扣费自上而下：先扣即将到期的赠送额度，再扣充值余额。',
  'billing.credits.error': '无法加载余额构成。',
  'billing.credits.empty': '暂无可用余额批次。',
  'billing.credits.column.kind': '来源',
  'billing.credits.column.remaining': '剩余',
  'billing.credits.column.amount': '入账金额',
  'billing.credits.column.expires': '到期',
  'billing.credits.column.issued': '入账时间',
  'billing.credits.kind.promo': '赠送',
  'billing.credits.kind.cash': '充值',
  'billing.credits.never_expires': '不过期',
  'billing.forbidden.title': '账务由企业管理员管理',
  'billing.forbidden.body':
    '成员可以管理自己的 API 密钥和用量，但企业余额、充值、额度入账与账务流水需要管理员账务权限。',
  'billing.forbidden.cta': '返回仪表盘',
  'billing.retry': '重试',
  'billing.topup.entry': '去充值',
  'billing.redeem.title': '兑换礼品卡',
  'billing.redeem.code_label': '礼品卡兑换码',
  'billing.redeem.code_placeholder': '32 位十六进制码',
  'billing.redeem.submit': '兑换',
  'billing.redeem.submitting': '兑换中…',
  'billing.redeem.error.code_required': '礼品卡兑换码为必填项',
  'billing.redeem.toast.success': '已到账 {amount}，当前余额 {balance}',
  'billing.transactions.title': '余额记录',
  'billing.transactions.latest': '最新',
  'billing.transactions.offset': '从第 {offset} 条开始',
  'billing.transactions.page': '第 {page} 页',
  'billing.transactions.loading': '正在加载交易…',
  'billing.transactions.error': '无法加载交易。',
  'billing.transactions.empty': '暂无交易。',
  'billing.transactions.column.date': '时间',
  'billing.transactions.column.type': '类型',
  'billing.transactions.column.amount_usd': '金额（CNY）',
  'billing.transactions.column.running': '余额',
  'billing.transactions.newer': '较新',
  'billing.transactions.older': '较旧',
  'billing.transactions.type.charge': '请求费用',
  'billing.transactions.type.gift_card_redeem': '礼品卡入账',
  'billing.transactions.type.gift_direct_issue': '赠送入账',
  'billing.transactions.type.gift_expiry_reclaim': '赠送到期回收',
  'billing.transactions.type.adjustment': '人工调整',
  'billing.transactions.type.topup': '充值入账',
  'billing.transactions.type.external_topup': '线下充值入账',
  'billing.pricing.title': '模型定价',
  'billing.pricing.unit': '每百万 Token · CNY',
  'billing.pricing.loading': '正在加载定价…',
  'billing.pricing.error': '无法加载定价。',
  'billing.pricing.empty': '暂时还没有可显示的模型价格。',
  'billing.pricing.column.vendor': '供应商',
  'billing.pricing.column.model': '模型',
  'billing.pricing.column.input': '输入',
  'billing.pricing.column.output': '输出',
  'billing.pricing.column.cache_read': '缓存命中',
  'billing.pricing.column.cache_write': '缓存写入',
  'billing.pricing.column.effective_from': '生效时间',

  // ── Billing statements (credit customers only) ─────────────────
  'billing.statements.title': '对账单',
  'billing.statements.subtitle': '按账期结算的应收账款。用量为参考信息，应付金额以期末应收为准。',
  'billing.statements.error': '无法加载对账单。',
  'billing.statements.column.period': '账期',
  'billing.statements.column.consumption': '用量（参考）',
  'billing.statements.column.closing_ar': '期末应收',
  'billing.statements.column.due': '到期日',
  'billing.statements.column.status': '状态',
  'billing.statements.status.settled': '已结清',
  'billing.statements.status.open': '未结清',
  'billing.statements.status.overdue': '已逾期',
  'billing.statements.detail.title': '对账单明细',
  'billing.statements.detail.period': '账期',
  'billing.statements.detail.consumption': '用量（参考）',
  'billing.statements.detail.opening_ar': '期初应收',
  'billing.statements.detail.payments': '已核销收款',
  'billing.statements.detail.closing_ar': '期末应收',
  'billing.statements.detail.cny_reference': '人民币参考金额',
  'billing.statements.detail.due': '到期日',
  'billing.statements.detail.close': '关闭',
  'billing.statements.detail.consumption_hint':
    '用量为该账期内的总消耗，仅作参考；应付金额以期末应收为准。',

  // ── Billing topup page ─────────────────────────────────────────
  'topup.title': '充值',
  'topup.subtitle': '使用微信支付为余额充值。下一步会显示二维码，你可以直接用手机扫码完成支付。',
  'topup.forbidden.title': '充值权限受限',
  'topup.forbidden.body': '只有团队所有者和管理员可以为余额充值。',
  'topup.form.caption': '充值订单',
  'topup.forbidden.back': '返回账单',
  'topup.amount.label': '金额（CNY · 整元）',
  'topup.amount.hint': 'CNY · 最小 {min} · 最大 {max} · 仅整元',
  'topup.usd.label': '将获得的余额价值',
  'topup.usd.hint': '按约 {rate} ¥/$ 折算；内部美元额度以下单时锁定的汇率为准。',
  'topup.usd.hint_loading': '正在读取当前服务端报价。',
  'topup.usd.hint_unavailable': '暂时无法读取报价；内部美元额度会在下单时由服务端锁定。',
  'topup.pay_type.legend': '支付方式',
  'topup.pay_type.alipay': '支付宝',
  'topup.pay_type.wxpay': '微信支付',
  'topup.error.whole_yuan': '输入整元金额（整数，不含小数）。',
  'topup.error.min': '最小充值为 {amount}。',
  'topup.error.max': '最大充值为 {amount}。',
  'topup.error.no_redirect': '支付通道未返回跳转地址。',
  'topup.cancel': '取消',
  'topup.submit': '前往支付 →',
  'topup.submitting': '创建中…',
  'topup.fee_hint': '汇率会定期刷新，支付通道手续费由 Nxtpath 承担。',
  'topup.qr.title': '扫码支付',
  'topup.qr.order_prefix': '订单 ',
  'topup.qr.image_alt': '支付二维码',
  'topup.qr.fallback_hint': '在手机上打开微信 App，扫描下方目标 URL。',
  'topup.qr.status.paid': '已收到支付，正在跳转…',
  'topup.qr.status.other': '订单 {status}',
  'topup.qr.status.pending': '等待支付…',
  'topup.qr.status.slow': '用时超过预期…',
  'topup.qr.slow_notice':
    '为节省带宽我们已停止轮询。若你已在 App 中完成支付，余额会自行更新，可安全关闭此弹窗。',
  'topup.qr.close': '关闭',

  // ── Settings page ──────────────────────────────────────────────
  'settings.title': '账号与团队',
  'settings.subtitle': '管理你的账号、团队成员和安全设置。',
  // 成员卡片不显示时用这一组：此时页面只剩个人部分，
  // 标题再提「团队」就是承诺一个他看不到的板块。
  'settings.title_personal': '账号',
  'settings.subtitle_personal': '管理你的账号和安全设置。',
  'settings.retry': '重试',
  'settings.account.title': '账号信息',
  'settings.account.description': '这里展示你当前账号最常用的信息。',
  'settings.account.email': '邮箱',
  'settings.account.account_id': '账户 ID',
  'settings.account.org_id': '团队 ID',
  'settings.account.email_verified': '邮箱验证状态',
  'settings.account.status.verified': '已验证',
  'settings.account.status.unverified': '未验证',
  'settings.technical.summary': '原始 ID 和其他技术字段会放在这里，便于排障时查看。',
  'settings.members.title': '团队成员',
  'settings.members.subtitle': '所有可以访问该团队的人。所有者和管理员可以邀请新成员。',
  'settings.members.invite_button': '邀请成员',
  'settings.members.loading': '正在加载成员…',
  'settings.members.error': '无法加载成员',
  'settings.members.empty': '暂无成员。',
  'settings.members.column.email': '邮箱',
  'settings.members.column.role': '角色',
  'settings.members.column.joined': '加入时间',
  'settings.members.column.actions': '操作',
  'settings.members.role.owner': '所有者',
  'settings.members.role.admin': '管理员',
  'settings.members.role.member': '成员',
  'settings.members.role.finance': '财务',
  'settings.members.model_permissions': '模型权限',
  'settings.members.unrestricted': '不限制',
  'settings.model_permissions.title': '成员模型权限',
  'settings.model_permissions.unit_title': '组织单元模型权限',
  'settings.model_permissions.eyebrow': '模型权限',
  'settings.model_permissions.unit_eyebrow': '模型权限 · 组织单元',
  'settings.model_permissions.subtitle': '配置 {member} 可以使用的模型。',
  'settings.model_permissions.unit_subtitle': '配置 {unit} 向下传递的模型。',
  'settings.model_permissions.authorized_count': '已授权 {count} 个',
  'settings.model_permissions.namespace_count': '{count} 个命名空间',
  'settings.model_permissions.mode.inherit': '继承',
  'settings.model_permissions.mode.all': '全部允许',
  'settings.model_permissions.mode.custom': '自定义',
  'settings.model_permissions.mode_help.inherit': '沿用上级策略。',
  'settings.model_permissions.mode_help.all': '允许当前以及后续新增的目录模型。',
  'settings.model_permissions.mode_help.custom': '只使用下方勾选的模型。',
  'settings.model_permissions.inherited_from': '来自 {source}',
  'settings.model_permissions.global_source': '团队默认策略',
  'settings.model_permissions.inherit_active': '正在继承 {source}',
  'settings.model_permissions.inherit_body': '切换到「自定义」可为该成员单独覆盖。',
  'settings.model_permissions.unit_inherit_body': '切换到「自定义」可为该组织单元单独覆盖。',
  'settings.model_permissions.all_models_active': '已允许全部模型',
  'settings.model_permissions.all_models_body': '目录中新增的模型会自动包含在内。',
  'settings.model_permissions.default_model': '默认 · {model}',
  'settings.model_permissions.search_label': '搜索模型权限',
  'settings.model_permissions.search_placeholder': '搜索命名空间、供应商或模型…',
  'settings.model_permissions.no_search_results': '没有匹配此搜索的命名空间或模型。',
  'settings.model_permissions.select_namespace': '全选',
  'settings.model_permissions.clear_namespace': '取消全选',
  'settings.model_permissions.legacy_title': '历史授权',
  'settings.model_permissions.legacy_body': '这些授权已无法对应目录中的模型；保存时会原样保留。',
  'settings.model_permissions.retry': '重试',
  'settings.model_permissions.close': '关闭模型权限',
  'settings.model_permissions.models': '个模型',
  'settings.model_permissions.default': '默认',
  'settings.model_permissions.empty': '没有可供授权的目录模型。',
  'settings.model_permissions.error': '无法加载模型权限',
  'settings.model_permissions.cancel': '取消',
  'settings.model_permissions.save': '保存权限',
  'settings.model_permissions.saving': '保存中…',
  'settings.model_permissions.toast.saved': '模型权限已保存',
  'settings.model_permissions.toast.unit_saved': '组织单元模型权限已保存',
  'settings.invitations.title': '待接受的邀请',
  'settings.invitations.loading': '加载中…',
  'settings.invitations.error': '无法加载邀请',
  'settings.invitations.empty': '暂无待接受的邀请。',
  'settings.invitations.column.email': '邮箱',
  'settings.invitations.column.role': '角色',
  'settings.invitations.column.invited_by': '邀请者',
  'settings.invitations.column.expires': '过期时间',
  'settings.invitations.column.actions': '操作',
  'settings.invitations.revoke': '撤销',
  'settings.invite_modal.title': '邀请成员',
  'settings.invite_modal.description': '受邀者会收到邮件，你也可以复制邀请链接自行分享。',
  'settings.invite_modal.email': '邮箱',
  'settings.invite_modal.role': '角色',
  'settings.invite_modal.role.member': '成员',
  'settings.invite_modal.role.admin': '管理员',
  'settings.invite_modal.role.finance': '财务',
  'settings.invite_modal.email_required': '邮箱为必填项',
  'settings.invite_modal.cancel': '取消',
  'settings.invite_modal.submit': '发送邀请',
  'settings.invite_modal.submitting': '发送中…',
  'settings.invite_modal.toast.sent': '邀请已发送',
  'settings.invite_link.title': '邀请已发送',
  'settings.invite_link.description': '收件人会收到包含下方链接的邮件。你也可以复制后手动分享。',
  'settings.invite_link.click_to_copy': '点击复制',
  'settings.invite_link.copied': '已复制到剪贴板',
  'settings.invite_link.copy_anywhere': '点击此区域任意位置即可复制',
  'settings.invite_link.done': '完成',
  'settings.invite_link.aria_label': '复制邀请链接到剪贴板',
  'settings.security.title': '安全',
  'settings.security.change_password': '修改登录密码',
  'settings.security.current_password': '当前密码',
  'settings.security.new_password': '新密码（10–128 位）',
  'settings.security.confirm_password': '确认新密码',
  'settings.security.submit': '修改密码',
  'settings.security.submitting': '保存中…',
  'settings.security.error.current_required': '当前密码为必填项',
  'settings.security.error.current_incorrect': '当前密码不正确',
  'settings.security.error.password_too_short': '密码长度至少 {min} 位',
  'settings.security.error.password_too_long': '密码长度最多 {max} 位',
  'settings.security.error.mismatch': '两次输入的密码不一致',
  'settings.security.error.network': '网络错误——重试一次',
  'settings.security.toast.success': '密码已修改',
  'settings.security.set_password.heading': '设置密码',
  'settings.security.set_password.body':
    '该账号目前通过邮箱验证码登录，尚未设置密码。我们会向你的邮箱发送 6 位验证码用于设置密码。',
  'settings.security.set_password.action': '发送验证码',
  'settings.security.set_password.action_cooldown': '发送验证码（{seconds} 秒）',
  'settings.security.set_password.sending': '发送中…',
  'settings.security.set_password.ack': '验证码已发送，{minutes} 分钟内有效。',
  'settings.security.set_password.code': '邮箱验证码',
  'settings.security.set_password.submit': '设置密码',
  'settings.security.set_password.toast.success': '密码已设置',
  'settings.security.set_password.error.format': '验证码是 6 位数字',
  'settings.security.set_password.error.invalid': '验证码不正确',
  'settings.security.set_password.error.expired': '验证码已过期，重新发送',
  'settings.security.set_password.error.attempts_exhausted': '尝试次数过多，重新发送验证码',
  'settings.security.set_password.error.send_rate_limited': '发送过于频繁，稍后再试',

  // ── Promoter binding (tenant #192) ──────────────────────────────
  'promoter.redeem.bound': '推广码已绑定',
  'promoter.redeem.grant_issued': '{amount} 推广赠送额度，{date} 到期',
  'promoter.redeem.grant_unconfigured': '赠送额度待运营配置',
  'promoter.billing.bound': '已绑定推广码 · {timestamp}',
  'promoter.grant.included': '含 {amount} 推广赠送额度',
  'promoter.grant.status_issued': '推广赠送额度已发放',
  'promoter.grant.expires_on': '{date} 到期',
  'promoter.grant.expiring_soon': '即将到期',

  // ── i18n switcher chrome ────────────────────────────────────────
  'i18n.switcher.aria_label': '语言',

  // ── Toast ───────────────────────────────────────────────────────
  'toast.region_aria': '通知',
  'toast.dismiss_aria': '关闭通知',

  // ── Billing topup return ────────────────────────────────────────
  'billing.topup_return.page.title': '充值结果',
  'billing.topup_return.page.order_label': '订单 ',
  'billing.topup_return.page.waiting_order_id': '等待订单号…',
  'billing.topup_return.loading.aria': '正在加载订单状态…',
  'billing.topup_return.missing.title': '缺少订单号',
  'billing.topup_return.missing.body':
    '此页面通常由支付服务商的回跳链接进入。如果你是误入，返回账单总览即可。',
  'billing.topup_return.missing.cta': '返回账单',
  'billing.topup_return.pending.title': '支付进行中',
  'billing.topup_return.pending.body': '支付正在处理。确认到账后本页会自动刷新——无需手动重载。',
  'billing.topup_return.slow.title': '支付确认比平常慢',
  'billing.topup_return.slow.body':
    '服务商尚未确认你的支付。如果已在 App 内完成支付，几分钟后余额会自动更新——可以安全关闭此页。',
  'billing.topup_return.slow.reference': '参考订单号：',
  'billing.topup_return.slow.cta.back': '返回账单',
  'billing.topup_return.slow.cta.support': '联系支持',
  'billing.topup_return.paid.title': '支付成功',
  'billing.topup_return.paid.credited': '到账',
  'billing.topup_return.paid.charged': '实付',
  'billing.topup_return.paid.at_rate': '汇率 {rate} ¥/$',
  'billing.topup_return.paid.new_balance': '新余额',
  'billing.topup_return.paid.balance_loading': '加载中…',
  'billing.topup_return.paid.technical': '技术账本明细',
  'billing.topup_return.paid.usd_credit': 'USD 入账',
  'billing.topup_return.paid.usd_ledger': 'USD 账本余额',
  'billing.topup_return.paid.redirect_hint': '数秒后将跳转至账单总览…',
  'billing.topup_return.paid.cta': '返回仪表盘',
  'billing.topup_return.terminal.expired.title': '订单已过期',
  'billing.topup_return.terminal.expired.body':
    '此订单在完成支付前已过期。如仍需充值，重新发起一笔。',
  'billing.topup_return.terminal.failed.title': '支付失败',
  'billing.topup_return.terminal.failed.body':
    '支付服务商报告此订单失败。未实际扣款，可以安全重试。',
  'billing.topup_return.terminal.refunded.title': '订单已退款',
  'billing.topup_return.terminal.refunded.body': '此订单已被退款。如仍需补充余额，重新发起充值。',
  'billing.topup_return.terminal.cta.retry': '重试',
  'billing.topup_return.terminal.cta.back': '返回账单',

  // ── Usage charts ────────────────────────────────────────────────
  'usage_chart.empty': '此时间范围暂无用量数据',
  'usage_chart.daily.aria':
    '每日 Token 用量 — {days} 天，合计 {total} Token（输入 {input}，输出 {output}）',
  'usage_chart.daily.tooltip':
    '{date} — {total} Token（输入 {input} + 输出 {output}），{requests} 次请求',

  // ── Skeleton ────────────────────────────────────────────────────
  'skeleton.loading': '加载中…',

  // ── Error banner ────────────────────────────────────────────────
  'error_banner.unexpected': '意外错误',
  'error.network': '网络异常，检查连接后重试。',
  'error.service': '服务异常，稍后重试。',
  'error.code.EMAIL_NOT_VERIFIED': '登录前需要先验证邮箱。',
  'error.code.INVALID_CREDENTIALS': '邮箱或密码不正确。',
  'error.code.ACCOUNT_SUSPENDED': '该账号已停用。',
  'error.code.ACCOUNT_DELETED': '该账号已注销。',
  'error.code.RATE_LIMITED': '操作太频繁，稍后再试。',
  'error.code.EMAIL_ALREADY_REGISTERED': '该邮箱已注册。',
  'error.code.EMAIL_FORMAT_INVALID': '邮箱格式不正确。',
  'error.code.PASSWORD_LENGTH_INVALID': '密码长度需为 10–128 位。',
  'error.code.INVALID_VERIFICATION_TOKEN': '验证链接无效。',
  'error.code.VERIFICATION_TOKEN_EXPIRED': '验证链接已过期。',
  'error.code.VERIFICATION_TOKEN_CONSUMED': '验证链接已使用。',
  'error.code.AUTH_REQUIRED': '继续操作前需要登录。',
  'error.code.FORBIDDEN': '没有执行该操作的权限。',
  'error.code.INVALID_PAYLOAD': '提交的内容不符合要求。',
  'error.code.CODE_INVALID': '验证码不正确。',
  'error.code.CODE_EXPIRED': '验证码已过期。',
  'error.code.CODE_RATE_LIMITED': '验证码尝试次数过多。',
  'error.code.INVITE_CODE_INVALID': '邀请码无效。',
  'error.code.INVITE_CODE_QUOTA_EXHAUSTED': '该邀请码今天的名额已用完。',
  'error.code.INVITATION_EXPIRED': '邀请已过期。',
  'error.code.INVITATION_REVOKED': '邀请已撤销。',
  'error.code.INVITATION_ALREADY_ACCEPTED': '邀请已被接受。',
  'error.code.INVITATION_NOT_FOUND': '找不到该邀请。',
  'error.code.INVITATION_CONFLICT': '邀请状态冲突。',
  'error.code.INSUFFICIENT_BALANCE': '余额不足。',
  'error.code.TOPUP_AMOUNT_TOO_SMALL': '充值金额低于下限。',
  'error.code.TOPUP_AMOUNT_TOO_LARGE': '充值金额超过上限。',
  'error.code.TOPUP_PROVIDER_ERROR': '支付通道暂时不可用。',
  'error.code.PAYMENT_ORDER_NOT_FOUND': '找不到该支付订单。',
  'error.code.PAYMENT_PROVIDER_CONFIG_NOT_FOUND': '支付通道尚未配置。',
  'error.code.GIFT_CARD_ALREADY_REDEEMED': '礼品卡已兑换。',
  'error.code.GIFT_CARD_EXPIRED': '礼品卡已过期。',
  'error.code.GIFT_CARD_NOT_FOUND': '找不到该礼品卡。',
  'error.code.GIFT_CARD_REVOKED': '礼品卡已作废。',
  'error.code.PROMOTER_CODE_INVALID': '推广码无效或已失效',
  'error.code.PROMOTER_ALREADY_BOUND': '该账号已绑定推广码',
  'error.code.PROMOTER_BIND_WINDOW_CLOSED': '绑定窗口已关闭',
  'error.code.PROMOTER_SELF_BIND_FORBIDDEN': '该账号不能绑定推广码',
  'error.code.PROMOTER_BIND_CONFLICT': '该账号已归属销售经理',
  'error.code.ORG_NOT_FOUND': '找不到该组织。',
  'error.code.ORG_ROLE_NOT_FOUND': '找不到该角色。',
  'error.code.ORG_ROLE_NAME_TAKEN': '角色名称已被占用。',
  'error.code.ORG_ROLE_IN_USE': '角色仍在使用，无法删除。',
  'error.code.KEY_NOT_FOUND': '找不到该密钥。',
  'error.code.API_KEY_NOT_FOUND': '找不到该 API 密钥。',
  'error.code.MODEL_ALIAS_NOT_FOUND': '找不到该模型别名。',
  'error.code.MODEL_ALIAS_ALREADY_EXISTS': '模型别名已存在。',
  'error.code.MODEL_MAPPING_STALE': '模型映射已变化，刷新后重试。',
  'error.code.MODEL_MAPPING_ROLLOUT_PENDING': '模型映射正在生效，稍后再试。',
  'error.code.MEMBER_MODEL_FORBIDDEN': '当前成员不能使用该模型。',
  'error.code.MEMBER_MONTHLY_LIMIT_EXCEEDED': '已超过本月成员额度。',
  'error.code.ATTACHMENT_NOT_FOUND': '找不到该附件。',
  'error.code.SERVICE_UNAVAILABLE': '服务暂时不可用。',
  'error.code.INTERNAL': '服务内部异常。',
  'error.code.NOT_IMPLEMENTED': '该功能尚未开放。',
  'error.code.TICKET_NOT_FOUND': '工单不存在或不属于当前账号',
  'error.code.TICKET_CLOSED': '工单已关闭。',
  'error.code.TICKET_LIMIT_EXCEEDED': '未结工单已达上限。',
  'error.code.ADMIN_KEY_LIMIT_REACHED': '该组织已有 20 个有效的管理密钥。撤销一个后再创建。',
  'clipboard.error.denied': '剪贴板访问被拒绝。',
  'clipboard.error.unavailable': '当前环境无法使用剪贴板。',
  'clipboard.error.rejected': '复制被拒绝，选中文本后自行复制。',
  'clipboard.error.failed': '复制失败，选中文本后自行复制。',

  // ── Support tickets (/tickets, tenant #116) ─────────────────────
  // 状态词对齐桌面端 gov 词条：待处理/处理中/已解决/已关闭。
  'tickets.title': '工单',
  'tickets.subtitle': '提交工单，跟进平台处理与回复',
  'tickets.create.caption': '提交工单',
  'tickets.create.type': '问题类型',
  'tickets.create.type.billing': '充值与账务',
  'tickets.create.type.usage': '用量与计费',
  'tickets.create.type.api': '密钥与接入',
  'tickets.create.type.model': '模型与质量',
  'tickets.create.type.other': '其他问题',
  'tickets.create.email': '联系邮箱',
  'tickets.create.email_hint': '回复会同步发送到该账号邮箱',
  'tickets.create.description': '问题内容',
  'tickets.create.description_placeholder': '描述问题现象、预期结果与相关请求 ID',
  'tickets.create.add_image': '添加图片',
  'tickets.create.remove_image': '移除 {filename}',
  'tickets.create.attach_hint': '最多 {max} 张图片，单张不超过 2MB，支持 PNG、JPG、WebP、GIF',
  'tickets.create.submit': '提交工单',
  'tickets.create.error.description_required': '问题内容为必填项',
  'tickets.create.error.description_too_long': '问题内容不能超过 {max} 字',
  'tickets.create.error.too_many_images': '最多上传 {max} 张图片',
  'tickets.create.error.image_type': '仅支持 PNG、JPG、WebP、GIF 图片',
  'tickets.create.error.image_size': '单张图片不能超过 2MB',
  'tickets.create.error.image_read': '图片 {filename} 读取失败',
  'tickets.create.toast.submitted': '工单已提交，平台会尽快处理并通过邮箱联系你',
  'tickets.create.toast.attachments_unavailable': '附件功能暂不可用，可先提交纯文字工单',
  'tickets.create.toast.limit': '未结工单已达上限，组织最多同时保留 20 个未关闭工单',
  'tickets.create.toast.rate_limited': '提交太频繁，每个账号每小时最多提交 5 个工单，稍后再试',
  'tickets.create.toast.failed': '工单提交失败',
  'tickets.status.all': '全部',
  'tickets.status.open': '待处理',
  'tickets.status.processing': '处理中',
  'tickets.status.resolved': '已解决',
  'tickets.status.closed': '已关闭',
  'tickets.list.caption': '我的工单',
  'tickets.list.filter_aria': '按状态筛选工单',
  'tickets.list.loading': '正在读取工单…',
  'tickets.list.error': '工单读取失败',
  'tickets.list.retry': '重试',
  'tickets.list.empty.title': '暂无工单',
  'tickets.list.empty.hint': '提交的工单与回复会显示在这里',
  'tickets.list.empty_filtered.title': '该状态下暂无工单',
  'tickets.list.empty_filtered.hint': '切回全部状态可查看所有工单',
  'tickets.list.attachments': '{count} 张图片',
  'tickets.list.pagination.summary': '第 {from}–{to} 条，共 {total} 条',
  'tickets.list.pagination.prev': '上一页',
  'tickets.list.pagination.next': '下一页',
  'tickets.list.pagination.aria': '工单分页',
  'tickets.error.not_found': '工单不存在或不属于当前账号',
  'tickets.replies.loading': '正在读取回复…',
  'tickets.replies.error': '回复读取失败：{message}',
  'tickets.replies.empty': '暂无回复',
  'tickets.replies.author.vendor': 'Nxtpath 官方',
  'tickets.replies.author.user': '我',
  'tickets.replies.placeholder': '补充说明或回复平台',
  'tickets.replies.closed_placeholder': '工单已关闭，不能追加回复',
  'tickets.replies.send': '回复',
  'tickets.replies.footer': '回复提交后不可修改或删除',
  'tickets.replies.toast.closed': '工单已关闭，不能追加回复',
  'tickets.replies.toast.rate_limited': '回复太频繁，每小时最多发送 30 条回复，稍后再试',
  'tickets.replies.toast.failed': '回复发送失败',
  'tickets.attachments.loading': '附件加载中…',
  'tickets.attachments.error': '附件加载失败',
  'tickets.attachments.lightbox.title': '查看大图',
  'tickets.attachments.lightbox.close': '关闭',

  // ── Notifications (/notifications + header bell, tenant #116) ───
  'notifications.title': '通知',
  'notifications.subtitle': '组织的余额、预算、工单与系统事件',
  'notifications.empty.title': '暂无通知',
  'notifications.empty.hint': '工单进展与平台事件会显示在这里',
  'notifications.unread_marker': '新',
  'notifications.open_ticket_aria': '打开工单页',
  'notifications.reply_prefix': '回复：',

  // ── Org Admin API (/admin-api) ──────────────────────────────────
  'adminApi.title': '组织管理 API',
  'adminApi.subtitle': '管理密钥，以及供脚本调用的 HTTP 接口。',
  'adminApi.tabs.aria': '组织管理 API 分区',
  'adminApi.tab.keys': '管理密钥',
  'adminApi.tab.reference': '接口文档',
  'adminApi.forbidden.title': '仅所有者和管理员可用',
  'adminApi.forbidden.body': '管理密钥使用所有者或管理员权限。成员和财务角色不能打开此页。',
  'adminApi.forbidden.cta': '返回总览',
  'adminApi.keys.explainer':
    '管理密钥以创建者的所有者或管理员权限行事。创建者失去该角色或离开组织后，密钥即失效。',
  'adminApi.keys.create': '创建密钥',
  'adminApi.keys.loading': '正在读取管理密钥…',
  'adminApi.keys.empty': '还没有管理密钥。',
  'adminApi.keys.column.name': '名称',
  'adminApi.keys.column.prefix': '密钥前缀',
  'adminApi.keys.column.created_by': '创建者',
  'adminApi.keys.column.created_at': '创建时间',
  'adminApi.keys.column.last_used': '最近使用',
  'adminApi.keys.column.status': '状态',
  'adminApi.keys.column.actions': '操作',
  'adminApi.keys.status.active': '有效',
  'adminApi.keys.status.revoked': '已撤销',
  'adminApi.keys.never_used': '从未使用',
  'adminApi.keys.revoke': '撤销',
  'adminApi.keys.create_modal.title': '创建管理密钥',
  'adminApi.keys.create_modal.description': '密钥明文只在下一屏显示一次。',
  'adminApi.keys.create_modal.name': '名称',
  'adminApi.keys.create_modal.name_hint': '1–64 个字符。显示在密钥列表中。',
  'adminApi.keys.create_modal.submit': '创建密钥',
  'adminApi.keys.create_modal.submitting': '正在创建…',
  'adminApi.keys.create_modal.cancel': '取消',
  'adminApi.keys.create_modal.name_invalid': '名称长度为 1–64 个字符。',
  'adminApi.keys.plaintext.title': '复制 {name}',
  'adminApi.keys.plaintext.warning': '这是此密钥唯一一次显示。现在复制。之后无法再次显示。',
  'adminApi.keys.plaintext.copy_aria': '复制管理密钥',
  'adminApi.keys.plaintext.copy': '复制密钥',
  'adminApi.keys.plaintext.copied': '已复制到剪贴板',
  'adminApi.keys.plaintext.close': '关闭',
  'adminApi.keys.plaintext.done': '已复制，关闭',
  'adminApi.keys.revoke_modal.title': '撤销管理密钥',
  'adminApi.keys.revoke_modal.body': '撤销 {name}？正在使用它的脚本会立即停止。',
  'adminApi.keys.revoke_modal.confirm': '撤销密钥',
  'adminApi.keys.revoke_modal.submitting': '正在撤销…',
  'adminApi.keys.revoke_modal.cancel': '取消',
  'adminApi.keys.revoke_modal.close': '关闭',
  'adminApi.keys.revoke_modal.type_label': '输入密钥名称以确认',
  'adminApi.doc.overview.body':
    '用管理密钥从脚本和服务器管理组织：成员月度限额、成员与角色、用量与请求日志，以及余额与账单。',
  'adminApi.doc.fact.base.title': '接口地址',
  'adminApi.doc.fact.base.note': '所有路径的前缀',
  'adminApi.doc.fact.auth.title': '认证方式',
  'adminApi.doc.fact.auth.note': '用管理密钥，不是 sk-',
  'adminApi.doc.fact.rate.title': '频率限制',
  'adminApi.doc.fact.rate.note': '按密钥计',
  'adminApi.doc.fact.rate.value': '120 次 / 60 秒',
  'adminApi.doc.base.copy': '复制',
  'adminApi.doc.auth.title': '认证',
  'adminApi.doc.auth.body':
    '发送 Authorization: Bearer 加上密钥，或把同一密钥放在 x-api-key 请求头。密钥形如 nxt-admin- 后接 48 位小写十六进制字符。若存在 Authorization，它必须是 Bearer 令牌，接口不会改读 x-api-key。',
  'adminApi.doc.auth.lifecycle':
    '在本页创建和撤销密钥。除撤销外没有过期时间。密钥仅在创建者仍是有效的所有者或管理员、且账号未被停用或删除时可用。创建者失去该角色、离开组织，或账号与组织已不存在时，调用返回 ADMIN_KEY_INACTIVE。未知或已撤销的密钥返回 ADMIN_KEY_INVALID。last_used_at 最多每分钟更新一次。',
  'adminApi.doc.auth.errors_title': '认证错误',
  'adminApi.doc.auth.err.required':
    '401 AUTH_REQUIRED — 请求头缺失，或不是格式正确的 nxt-admin- 密钥。模型密钥（sk-）也是这个错误，不是 ADMIN_KEY_INVALID。',
  'adminApi.doc.auth.err.invalid': '401 ADMIN_KEY_INVALID — 密钥不存在或已经撤销。',
  'adminApi.doc.auth.err.inactive':
    '403 ADMIN_KEY_INACTIVE — 密钥仍在，但创建者已不是有效的所有者或管理员，或账号与组织已不存在。',
  'adminApi.doc.auth.err.rate':
    '429 RATE_LIMITED — 每把密钥 60 秒内 120 次请求。桶名为 org-admin-api-key。OpenAPI 文档本身不计入该限制。',
  'adminApi.doc.conventions.title': '约定',
  'adminApi.doc.conventions.snake':
    'JSON 字段为 snake_case。控制台其余会话接口仍是 camelCase，此接口不是。',
  'adminApi.doc.conventions.money':
    '金额以 *_usd_micros 的整数微美元为准，*_usd_cents 是其向下取整后的整数美分。*_usd 是用于展示的十进制字符串，例如 "12.34"。',
  'adminApi.doc.conventions.time':
    '时间戳为 ISO-8601 UTC。用量的日标签和账单的账期是日历日期，字段说明会标明时区。',
  'adminApi.doc.conventions.page':
    '列表接口接受 limit（1–100，默认 50）和 offset（非负整数，默认 0；大于 10000 会被拒绝），并返回 { data, has_more, next_offset }。最后一页的 next_offset 为 null。GET /roles 是例外：一次返回全部角色，has_more 为 false，next_cursor 为 null，且不接受分页参数。',
  'adminApi.doc.conventions.window':
    'from 与 to 是 ISO 日期（YYYY-MM-DD，按 UTC 零点解读）或 ISO 时间。两者都传时，from 必须早于 to，且跨度最多 90 天。可以只传其中一个。usage summary、daily usage 和请求日志上，非空的 model 查询会被拒绝。',
  'adminApi.doc.conventions.error':
    '失败响应使用同一信封。retryable 仅在限流等暂时失败时为 true。400 INVALID_PAYLOAD 表示查询或请求体不合法。403 FORBIDDEN 表示所有者受保护。404 表示成员或密钥不在本组织。503 SERVICE_UNAVAILABLE 表示限流绑定缺失。',
  'adminApi.doc.conventions.error_shape': '错误信封',
  'adminApi.doc.endpoints.title': '接口目录',
  'adminApi.doc.toc.aria': '接口文档目录',
  'adminApi.doc.index.label': '接口目录',
  'adminApi.doc.group.key': '密钥',
  'adminApi.doc.group.members': '成员与角色',
  'adminApi.doc.group.usage': '用量与请求日志',
  'adminApi.doc.group.billing': '余额与账单',
  'adminApi.doc.params.query': '查询参数',
  'adminApi.doc.params.body': '请求体',
  'adminApi.doc.params.response': '响应',
  'adminApi.doc.params.none': '无。',
  'adminApi.doc.params.name': '字段',
  'adminApi.doc.params.detail': '说明',
  'adminApi.doc.curl': 'curl 示例',
  'adminApi.doc.openapi.title': 'OpenAPI 文档',
  'adminApi.doc.openapi.body': '机器可读文档是公开的。不需要密钥，也不计入限流。',
  'adminApi.doc.openapi.link': '打开 openapi.json',
  'adminApi.doc.f.limit': '每页条数。整数 1–100。默认 50。',
  'adminApi.doc.f.offset': '跳过的行数。非负整数。默认 0。大于 10000 会被拒绝。',
  'adminApi.doc.f.from': '时间窗起点。ISO 日期或时间。可选。',
  'adminApi.doc.f.to': '时间窗终点。两者都传时必须晚于 from。可选。',
  'adminApi.doc.f.account_id': '只返回某一个成员账号。可选。',
  'adminApi.doc.f.model_rejected':
    '会被拒绝。非空值返回 400 INVALID_PAYLOAD。这些组织级查询不能按模型过滤。',
  'adminApi.doc.f.model_ok': '可选的模型过滤。仅此按成员用量查询支持。',
  'adminApi.doc.f.tz':
    '切分日桶所用的 IANA 时区。省略则为 UTC。无法识别的时区返回 400 INVALID_PAYLOAD。',
  'adminApi.doc.f.has_more': '本页之后还有数据时为 true。',
  'adminApi.doc.f.next_offset': '下一页应传的 offset。最后一页为 null。',
  'adminApi.doc.f.page_data': '本页的行。',
  'adminApi.doc.f.money.micros': '该金额的权威整数微美元。',
  'adminApi.doc.f.money.cents': '整数美分，为对应微美元字段向下取整。',
  'adminApi.doc.f.money.decimal': '微美元金额的十进制字符串，用于展示。',
  'adminApi.doc.f.member.account_id': '成员账号 ID。',
  'adminApi.doc.f.member.email': '邮箱。',
  'adminApi.doc.f.member.display_name': '显示名。没有则为 null。',
  'adminApi.doc.f.member.role': '内置角色；若分配了自定义角色，则为角色 ID（orl_…）。',
  'adminApi.doc.f.member.status': 'active、disabled 或 deleted。',
  'adminApi.doc.f.member.limit': '月度上限，整数美分。没有上限时为 null。',
  'adminApi.doc.f.member.joined_at': '成员加入的 ISO-8601 UTC 时间。',
  'adminApi.doc.f.status_filter':
    '可选。active、disabled 或 deleted。其他值返回 400 INVALID_PAYLOAD。',
  'adminApi.doc.ep.me.summary':
    '返回这把管理密钥所属的组织和它的限流额度。可用来确认密钥是否可用。',
  'adminApi.doc.ep.me.title': '查看当前密钥',
  'adminApi.doc.f.me.id': '管理密钥 ID。',
  'adminApi.doc.f.me.name': '创建时填写的名称。',
  'adminApi.doc.f.me.prefix': '密钥开头的若干字符。不是明文。',
  'adminApi.doc.f.me.org_id': '组织 ID。',
  'adminApi.doc.f.me.org_name': '组织名称。',
  'adminApi.doc.f.me.created_by': '创建该密钥的所有者或管理员账号 ID。',
  'adminApi.doc.f.me.bucket': '限流桶名称。固定为 org-admin-api-key。',
  'adminApi.doc.f.me.limit': '每个窗口允许的请求数。为 120。',
  'adminApi.doc.f.me.window': '窗口长度，单位秒。为 60。',
  'adminApi.doc.ep.members.summary':
    '返回组织成员及其角色、状态和月度限额。要看本月已花多少，用「按成员用量」。',
  'adminApi.doc.ep.members.title': '列出成员',
  'adminApi.doc.ep.member.summary': '按账号 ID 查询一个成员的角色、状态和月度限额。',
  'adminApi.doc.ep.member.title': '查看单个成员',
  'adminApi.doc.ep.member_patch.summary':
    '给成员设置月度消费上限、更换角色，或停用 / 启用成员。只传要改的字段。',
  'adminApi.doc.ep.member_patch.title': '修改成员限额、角色或状态',
  'adminApi.doc.ep.member_patch.highlight':
    '限额：正整数（美分），传 null 取消上限；不接受 0；要停止消费，停用该成员。角色：admin、member、finance 或自定义角色 ID（orl_…），不能设为 owner。状态：active 或 disabled。所有者的角色和状态都不能改。',
  'adminApi.doc.f.patch.limit': '正整数美分，或用 null 清除。0 和负数返回 400 INVALID_PAYLOAD。',
  'adminApi.doc.f.patch.role':
    'admin、member、finance，或自定义角色 ID（orl_…）。owner 会被拒绝。不能给自己一个缺少 org:members:manage 的角色。',
  'adminApi.doc.f.patch.status': 'active 或 disabled。deleted 会被拒绝。不能停用所有者。',
  'adminApi.doc.f.patch.member':
    '变更后的成员。嵌套字段与 GET /v1/admin/members/{account_id} 相同。',
  'adminApi.doc.f.patch.revision': '变更后的缓存版本。网关未报告时为 null。',
  'adminApi.doc.f.patch.convergence': '校验密钥缓存已跟上时为 immediate，否则为 degraded。',
  'adminApi.doc.f.patch.effective_after': '预计模型流量能看到此次变更的 ISO-8601 UTC 时间。',
  'adminApi.doc.ep.roles.summary': '返回组织里可分配的角色（内置与自定义）、各自的权限和人数。',
  'adminApi.doc.ep.roles.title': '列出角色',
  'adminApi.doc.f.roles.id': '内置角色名，或自定义角色 ID（orl_…）。',
  'adminApi.doc.f.roles.name': '角色显示名。',
  'adminApi.doc.f.roles.builtin': '内置角色为 true。',
  'adminApi.doc.f.roles.permissions': '该角色授予的权限名。',
  'adminApi.doc.f.roles.member_count': '当前被分配该角色的成员数。',
  'adminApi.doc.f.roles.has_more': '始终为 false。此列表不分页。',
  'adminApi.doc.f.roles.next_cursor': '始终为 null。角色列表不使用 offset 信封。',
  'adminApi.doc.ep.usage_summary.summary': '返回一段时间内整个组织的请求数、Token 数和花费合计。',
  'adminApi.doc.ep.usage_summary.title': '组织用量合计',
  'adminApi.doc.f.usage.org_id': '合计所属的组织 ID。',
  'adminApi.doc.f.usage.requests': '时间窗内的请求数。',
  'adminApi.doc.f.usage.input': '输入 Token。',
  'adminApi.doc.f.usage.output': '输出 Token。思考单独计费的通道上，这里只含可见输出。',
  'adminApi.doc.f.usage.cache_read': '缓存读取 Token。',
  'adminApi.doc.f.usage.cache_write': '缓存写入 Token。',
  'adminApi.doc.f.usage.window_from': '实际汇总窗口的 ISO-8601 起点，含该时刻。',
  'adminApi.doc.f.usage.window_to': '实际汇总窗口的 ISO-8601 终点，不含该时刻。',
  'adminApi.doc.ep.usage_daily.summary': '按天返回组织的请求数、Token 数和花费，可只看某个成员。',
  'adminApi.doc.ep.usage_daily.title': '按日用量',
  'adminApi.doc.f.daily.timezone': 'days[].date 所采用的 IANA 时区。省略 tz 时为 UTC。',
  'adminApi.doc.f.daily.date': 'timezone 中的 YYYY-MM-DD。不一定是 UTC 日期。',
  'adminApi.doc.ep.usage_members.summary':
    '按成员返回一段时间内的用量和花费，以及本月限额的使用进度。',
  'adminApi.doc.ep.usage_members.title': '按成员用量',
  'adminApi.doc.ep.usage_members.note':
    '每行还带成功 / 错误 / 限流次数、缓存命中率、平均延迟、部门、主模型与默认模型、authorized_models 和 trend_14d（近 14 天趋势）。未设限额时，剩余额度和使用比例为 null；超额时剩余额度为 0、比例为 1。',
  'adminApi.doc.f.seats.account_id': '该席位的成员账号 ID。',
  'adminApi.doc.f.seats.name': '成员显示名。没有则为 null。',
  'adminApi.doc.f.seats.total_tokens': '该席位在时间窗内报告的 Token 合计。',
  'adminApi.doc.f.seats.used':
    '本月至今的消费，整数美分，由该月微美元向下取整。窗口不是本月时，它与窗口内费用不是同一个数。',
  'adminApi.doc.f.seats.remaining':
    'monthly_limit_usd_cents 减去 monthly_used_usd_cents，最小为 0。席位没有限额时为 null。超出上限后它保持 0，已用金额仍会继续增加。',
  'adminApi.doc.f.seats.ratio': '已用除以限额，按微美元计算，最大为 1。没有限额时为 null。',
  'adminApi.doc.ep.requests.summary':
    '逐条返回组织的 API 请求：调用的模型、Token、花费、状态码和耗时。可只看某个成员。',
  'adminApi.doc.ep.requests.title': '请求日志',
  'adminApi.doc.ep.requests.note':
    '以下字段只在请求涉及时出现：reasoning_tokens、image_input_tokens、rating_status、unit_billing、token_billing、archived。rating_status 为 pending 时，金额还不是最终值。',
  'adminApi.doc.f.requests.id': '存储的行 ID。',
  'adminApi.doc.f.requests.request_id': '网关请求 ID。',
  'adminApi.doc.f.requests.key_prefix': '发起该请求的模型密钥前缀。不是管理密钥。',
  'adminApi.doc.f.requests.vendor': '处理该请求的供应商。',
  'adminApi.doc.f.requests.model': '该请求记录的模型名。',
  'adminApi.doc.f.requests.status_code': '返回给调用方的 HTTP 状态码。',
  'adminApi.doc.f.requests.latency': '延迟，单位毫秒。未记录时为 null。',
  'adminApi.doc.f.requests.created_at': '该行创建的 ISO-8601 UTC 时间。',
  'adminApi.doc.ep.balance.summary': '返回组织当前的余额。',
  'adminApi.doc.ep.balance.title': '查询余额',
  'adminApi.doc.ep.balance.note':
    '能区分时，还会返回赠送 / 现金拆分（promo_*、cash_*）和 credit 对象。这些字段缺失表示无法拆分，不代表金额为 0。',
  'adminApi.doc.f.balance.account_id': '余额所记的账号 ID。',
  'adminApi.doc.f.balance.updated_at': '余额行更新的 ISO-8601 UTC 时间。',
  'adminApi.doc.ep.transactions.summary': '逐条返回余额的每一次变动，可按时间筛选。',
  'adminApi.doc.ep.transactions.title': '余额流水',
  'adminApi.doc.ep.transactions.note':
    '入账为正、扣费为负，单位微美元。type 为 archived_charges 的行只是已归档区间的占位，不是又一笔扣费，汇总时不要计入。',
  'adminApi.doc.f.tx.id': '流水 ID。',
  'adminApi.doc.f.tx.type': '变动类型。未知字符串按不透明值处理。archived_charges 是缺口行。',
  'adminApi.doc.f.tx.reason': '原因文本。没有则为 null。',
  'adminApi.doc.f.tx.actor_type': '写入该条的操作者类型。',
  'adminApi.doc.f.tx.actor_user_id': '操作者用户 ID。没有则为 null。',
  'adminApi.doc.f.tx.external_request_id': '外部请求 ID。没有则为 null。',
  'adminApi.doc.ep.statements.summary': '返回组织的账单：每个账期的用量、欠款和已核销的回款。',
  'adminApi.doc.ep.statements.title': '账单',
  'adminApi.doc.ep.statements.note':
    'consumption：账期总用量，仅供参考。opening_ar：上一期期末欠款。closing_ar：本期截止时的欠款，即 max(0, −余额)。payments_applied：本期核销的回款。账期日期和 due_date 按 UTC+8 计，period_end 不含当天。未设出账汇率时 cny_reference_closing_ar_cents 为 null。',
  'adminApi.doc.f.statements.id': '账单 ID。',
  'adminApi.doc.f.statements.period_start': '账期起点，含当天。UTC+8 日历日期 YYYY-MM-DD。',
  'adminApi.doc.f.statements.period_end': '账期终点，不含当天。UTC+8 日历日期 YYYY-MM-DD。',
  'adminApi.doc.f.statements.cny':
    '按出账时政策汇率折算的期末应收，单位人民币分。未设置时为 null。',
  'adminApi.doc.f.statements.due_date': 'UTC+8 日历日期，等于账期终点加上当时生效的宽限天数。',
  'adminApi.doc.f.statements.status': '账单状态。',
  'adminApi.doc.f.statements.closed_at': '账单关闭的 ISO-8601 UTC 时间。未关闭时为 null。',
};
